import { Injectable, OnDestroy } from '@angular/core';
import {
	EscalationData as EscalationDataDTO, EscalationModel, Escalations, EstimateGrossMargin,
	EstimateMetaData, FeesData, IAreaChild, IHierarchyItem,
	IProjectHierarchyItem, IndividualTravelExpenses, LaborConstants,
	LaborMetaData, MiscMetaData, OtPwrSalesOffices, OverTimeRates, RatesRequestModel,
	RatesResponseModel, SalesOfficeLaborRates, SQWebArea, SQWebController, SQWebDesigoOptic,
	SQWebPanel, SQWebProject, SQWebSystem, SubSystem, SystemComponent, TravelRates,
	LaborLevelwiseData, MiscLevelwiseData, LaborRates, Material, MaterialLevelwiseData, EstimateData,
	TravelExpenses, TravelExpensesCollection,
	MaterialData, SQWebMaterialCategory, Subcontractors
} from '../../models/dtos';
import { CONSTANTS } from '../../Constants/Constants';
import { OpenEstimateApiService } from '../api/open-estimate.service';
import { CommonDataService } from '../common-data.service';
import { LaborCalculationResult } from '../../models/common/interfaces/LaborCalculationResult';
import { MaterialCalculationResult } from '../../models/common/interfaces/MaterialCalculationResult';
import { MiscCalculationResult } from '../../models/common/interfaces/MiscCalculationResult';
import { HierarchyCosts } from '../../models/common/interfaces/HierarchyCosts';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EstimateCommonFunctionsService } from '../estimate/estimate-common-functions.service';
import { DynamicFeeCostInputs } from '../../models/sol-estimate-table/DynamicFeeCostInputs';
import { roundHalfEven9, roundHalfEvenToPrecision } from '../../../types/number-extensions';
import { CommonSynchronizationDataService } from '../common/common-synchronization-data.service';
import { MaterialSubCategory, SubSidyProgramMaterialType } from '../../models/enum';
import { CommonMetaDataService } from '../common-meta-data.service';
import { ILevelSalesOfficeLaborData } from '../../models/ILevelSalesOfficeLaborData';

// Union type for leaf nodes that have material cost
type MaterialNode = SQWebController | SQWebPanel | SQWebDesigoOptic;

// Material type constants for categorization (EN and FR)
const MATERIAL_TYPE_SIEMENS = new Set(['Siemens Material', 'Matériel Siemens']);
const MATERIAL_TYPE_OUTSIDE = new Set(['Outside Material', 'Matériel extérieur']);
const MATERIAL_TYPE_MECHANICAL = new Set(['Mechanical Material', 'Matériel mécanique']);
const MATERIAL_TYPE_ELECTRICAL = new Set(['Electrical Material', 'Matériel électrique']);
const MATERIAL_AT_TYPE_SIEMENS = new Set(['SI OEM Material', 'Matériel SI OEM']);

@Injectable({
	providedIn: 'root'
})
export class SolLevelCostCalculationService implements OnDestroy {

	private project: SQWebProject;
	private estimateData: EstimateData;
	private laborMetadata: LaborMetaData;
	private estimateMetaData: EstimateMetaData;
	private laborLevelwiseData: LaborLevelwiseData;
	private miscLevelwiseData: MiscLevelwiseData;
	private materialLevelwiseData: MaterialLevelwiseData;
	private miscMetaData: MiscMetaData;
	private presentLaborRates: SalesOfficeLaborRates;
	private laborConstants: LaborConstants;
	private newEscalationRates: EscalationModel;
	private feesData: FeesData;
	private proposalAreaList: { id: string, description: string }[] = [];
	private estimateSubContractorMap: Map<string, Subcontractors> = new Map();
	// A hidden project collects no subcontractors, which empties the estimate array; this keeps the last
	// rows' edits so they can be restored when the project is unhidden.
	private retainedSubContractorState = new Map<string, Subcontractors>();
	// Project material costs after escalation but before subsidy, so subsidy can be re-applied without re-walking the hierarchy.
	private preSubsidyMaterialCosts: Record<MaterialSubCategory, number> | null = null;
	private lastProductivityReserve: number = CONSTANTS.ZERO;
	private laborByLevelAndSalesOffice = new Map<string, ILevelSalesOfficeLaborData>();
	// Per-run lookups; reset in calculateHierarchyCosts and released in clearLevelWiseCollections.
	private laborRatesIndex: { source: LaborRates[]; byOffice: Map<number, LaborRates> } | null = null;
	private readonly zoneOfficeIdsCache = new Map<string, number[]>();
	private readonly destroy$ = new Subject<void>();
	private isEstimateOpenedCostCalculation: boolean = false;

	constructor(
		private readonly commonDataService: CommonDataService,
		private readonly openEstimateApiService: OpenEstimateApiService,
		private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
		private readonly commonSynchronizationService: CommonSynchronizationDataService,
		private readonly commonMetaDataService: CommonMetaDataService) { }

	public setLevelWiseCollections(laborLevelwiseData: LaborLevelwiseData,
		miscLevelwiseData: MiscLevelwiseData,
		materialLevelwiseData: MaterialLevelwiseData
	): void {
		this.laborLevelwiseData = laborLevelwiseData;
		this.miscLevelwiseData = miscLevelwiseData;
		this.materialLevelwiseData = materialLevelwiseData;
	}

	/**
	 * Clears the in-memory level-wise collections after calculations are complete.
	 * This frees the large data sets from memory since they are persisted in IndexDB.
	 */
	public clearLevelWiseCollections(): void {
		this.laborLevelwiseData = null;
		this.miscLevelwiseData = null;
		this.materialLevelwiseData = null;
		this.laborRatesIndex = null;
		this.zoneOfficeIdsCache.clear();
	}

	/**
	 * Converts known N/A labor values to zero before calculating costs and hours.
	 */
	private getNACheckedLaborValue(laborValue: number): number {
		return CONSTANTS.SENTINEL_LABOR_VALUES.includes(laborValue) ? CONSTANTS.ZERO : laborValue;
	}

	/**
	 * Creates an empty LaborCalculationResult
	 */
	private createEmptyLaborResult(): LaborCalculationResult {
		return {
			totalLaborCost: CONSTANTS.ZERO,
			pmLaborCost: CONSTANTS.ZERO,
			programmingLaborCost: CONSTANTS.ZERO,
			graphicsLaborCost: CONSTANTS.ZERO,
			digitalDeploymentLaborCost: CONSTANTS.ZERO,
			engineeringLaborCost: CONSTANTS.ZERO,
			specialistLaborCost: CONSTANTS.ZERO,
			mechanicalLaborCost: CONSTANTS.ZERO,
			electricalLaborCost: CONSTANTS.ZERO,
			networkEngineeringLaborCost: CONSTANTS.ZERO,
			qualificationEngineeringLaborCost: CONSTANTS.ZERO,
			cyberSecEngineeringLaborCost: CONSTANTS.ZERO,
			energyEngineeringLaborCost: CONSTANTS.ZERO,
			lEngineeringLaborCost: CONSTANTS.ZERO,
			// Hours
			pmHours: CONSTANTS.ZERO,
			programmingHours: CONSTANTS.ZERO,
			graphicsHours: CONSTANTS.ZERO,
			digitalDeploymentHours: CONSTANTS.ZERO,
			engineeringHours: CONSTANTS.ZERO,
			specialistHours: CONSTANTS.ZERO,
			mechanicalHours: CONSTANTS.ZERO,
			electricalHours: CONSTANTS.ZERO,
			networkEngineeringHours: CONSTANTS.ZERO,
			qualificationEngineeringHours: CONSTANTS.ZERO,
			cyberSecEngineeringHours: CONSTANTS.ZERO,
			energyEngineeringHours: CONSTANTS.ZERO,
			lEngineeringHours: CONSTANTS.ZERO
		};
	}

	/**
	 * Creates an empty MaterialCalculationResult
	 */
	private createEmptyMaterialResult(): MaterialCalculationResult {
		return {
			totalMaterialCost: CONSTANTS.ZERO,
			siemensMaterialCost: CONSTANTS.ZERO,
			outsideMaterialCost: CONSTANTS.ZERO,
			mechanicalMaterialCost: CONSTANTS.ZERO,
			electricalMaterialCost: CONSTANTS.ZERO,
			actualTotalMaterialCost: CONSTANTS.ZERO,
			actualSiemensMaterialCost: CONSTANTS.ZERO,
			actualOutsideMaterialCost: CONSTANTS.ZERO,
			actualMechanicalMaterialCost: CONSTANTS.ZERO,
			actualElectricalMaterialCost: CONSTANTS.ZERO
		};
	}

	/**
	 * Creates an empty MiscCalculationResult
	 */
	private createEmptyMiscResult(): MiscCalculationResult {
		return {
			totalMiscellaneousCost: CONSTANTS.ZERO,
			totalNonRiskCost: CONSTANTS.ZERO,
			totalRiskCost: CONSTANTS.ZERO,
			totalSubcontractorCost: CONSTANTS.ZERO
		};
	}

	/**
	 * Adds two LaborCalculationResult objects together
	 */
	private addLaborResults(parentTotalCost: LaborCalculationResult, childTotalCost: LaborCalculationResult): LaborCalculationResult {
		return {
			totalLaborCost: roundHalfEven9(parentTotalCost.totalLaborCost + childTotalCost.totalLaborCost),
			pmLaborCost: roundHalfEven9(parentTotalCost.pmLaborCost + childTotalCost.pmLaborCost),
			programmingLaborCost: roundHalfEven9(parentTotalCost.programmingLaborCost + childTotalCost.programmingLaborCost),
			graphicsLaborCost: roundHalfEven9(parentTotalCost.graphicsLaborCost + childTotalCost.graphicsLaborCost),
			digitalDeploymentLaborCost: roundHalfEven9(parentTotalCost.digitalDeploymentLaborCost + childTotalCost.digitalDeploymentLaborCost),
			engineeringLaborCost: roundHalfEven9(parentTotalCost.engineeringLaborCost + childTotalCost.engineeringLaborCost),
			specialistLaborCost: roundHalfEven9(parentTotalCost.specialistLaborCost + childTotalCost.specialistLaborCost),
			mechanicalLaborCost: roundHalfEven9(parentTotalCost.mechanicalLaborCost + childTotalCost.mechanicalLaborCost),
			electricalLaborCost: roundHalfEven9(parentTotalCost.electricalLaborCost + childTotalCost.electricalLaborCost),
			networkEngineeringLaborCost: roundHalfEven9(parentTotalCost.networkEngineeringLaborCost + childTotalCost.networkEngineeringLaborCost),
			qualificationEngineeringLaborCost: roundHalfEven9(parentTotalCost.qualificationEngineeringLaborCost + childTotalCost.qualificationEngineeringLaborCost),
			cyberSecEngineeringLaborCost: roundHalfEven9(parentTotalCost.cyberSecEngineeringLaborCost + childTotalCost.cyberSecEngineeringLaborCost),
			energyEngineeringLaborCost: roundHalfEven9(parentTotalCost.energyEngineeringLaborCost + childTotalCost.energyEngineeringLaborCost),
			lEngineeringLaborCost: roundHalfEven9(parentTotalCost.lEngineeringLaborCost + childTotalCost.lEngineeringLaborCost),
			// Hours
			pmHours: roundHalfEven9(parentTotalCost.pmHours + childTotalCost.pmHours),
			programmingHours: roundHalfEven9(parentTotalCost.programmingHours + childTotalCost.programmingHours),
			graphicsHours: roundHalfEven9(parentTotalCost.graphicsHours + childTotalCost.graphicsHours),
			digitalDeploymentHours: roundHalfEven9(parentTotalCost.digitalDeploymentHours + childTotalCost.digitalDeploymentHours),
			engineeringHours: roundHalfEven9(parentTotalCost.engineeringHours + childTotalCost.engineeringHours),
			specialistHours: roundHalfEven9(parentTotalCost.specialistHours + childTotalCost.specialistHours),
			mechanicalHours: roundHalfEven9(parentTotalCost.mechanicalHours + childTotalCost.mechanicalHours),
			electricalHours: roundHalfEven9(parentTotalCost.electricalHours + childTotalCost.electricalHours),
			networkEngineeringHours: roundHalfEven9(parentTotalCost.networkEngineeringHours + childTotalCost.networkEngineeringHours),
			qualificationEngineeringHours: roundHalfEven9(parentTotalCost.qualificationEngineeringHours + childTotalCost.qualificationEngineeringHours),
			cyberSecEngineeringHours: roundHalfEven9(parentTotalCost.cyberSecEngineeringHours + childTotalCost.cyberSecEngineeringHours),
			energyEngineeringHours: roundHalfEven9(parentTotalCost.energyEngineeringHours + childTotalCost.energyEngineeringHours),
			lEngineeringHours: roundHalfEven9(parentTotalCost.lEngineeringHours + childTotalCost.lEngineeringHours)
		};
	}

	private setTotalLaborCost(laborCostResult: LaborCalculationResult): void {
		laborCostResult.totalLaborCost = roundHalfEven9(laborCostResult.pmLaborCost +
			laborCostResult.programmingLaborCost +
			laborCostResult.graphicsLaborCost +
			laborCostResult.digitalDeploymentLaborCost +
			laborCostResult.engineeringLaborCost +
			laborCostResult.specialistLaborCost +
			laborCostResult.mechanicalLaborCost +
			laborCostResult.electricalLaborCost +
			laborCostResult.networkEngineeringLaborCost +
			laborCostResult.qualificationEngineeringLaborCost +
			laborCostResult.cyberSecEngineeringLaborCost +
			laborCostResult.energyEngineeringLaborCost +
			laborCostResult.lEngineeringLaborCost);
	}

	private cloneLaborBySalesOfficeMap(sourceMap: Map<number, LaborCalculationResult>): Map<number, LaborCalculationResult> {
		const clonedMap = new Map<number, LaborCalculationResult>();
		sourceMap.forEach((value, key) => {
			clonedMap.set(key, { ...value });
		});
		return clonedMap;
	}

	private mergeLaborBySalesOfficeMaps(targetMap: Map<number, LaborCalculationResult>, sourceMap: Map<number, LaborCalculationResult>): void {
		sourceMap.forEach((value, key) => {
			const existing = targetMap.get(key) ?? this.createEmptyLaborResult();
			targetMap.set(key, this.addLaborResults(existing, value));
		});
	}

	private addOfficeLaborContribution(
		officeLaborMap: Map<number, LaborCalculationResult>,
		salesOfficeId: number,
		contribution: LaborCalculationResult
	): void {
		const existing = officeLaborMap.get(salesOfficeId) ?? this.createEmptyLaborResult();
		officeLaborMap.set(salesOfficeId, this.addLaborResults(existing, contribution));
	}

	private setLaborBySalesOfficeAtLevel(
		projectHierarchyItem: IProjectHierarchyItem,
		sumUpLaborBySalesOffice: Map<number, LaborCalculationResult>,
		individualLaborBySalesOffice: Map<number, LaborCalculationResult>
	): void {
		// Only the project level is ever read back (rebuildTravelExpensesFromLaborHours); cloning every area/system is wasted work.
		if (projectHierarchyItem.itemUniqueId !== this.project?.itemUniqueId) {
			return;
		}
		this.laborByLevelAndSalesOffice.set(projectHierarchyItem.itemUniqueId, {
			sumUpLaborBySalesOffice: this.cloneLaborBySalesOfficeMap(sumUpLaborBySalesOffice),
			individualLaborBySalesOffice: this.cloneLaborBySalesOfficeMap(individualLaborBySalesOffice)
		});
	}

	/**
	 * Adds two MaterialCalculationResult objects together
	 */
	private addMaterialResults(parentTotalCost: MaterialCalculationResult, childTotalCost: MaterialCalculationResult): MaterialCalculationResult {
		return {
			totalMaterialCost: roundHalfEven9(parentTotalCost.totalMaterialCost + childTotalCost.totalMaterialCost),
			siemensMaterialCost: roundHalfEven9(parentTotalCost.siemensMaterialCost + childTotalCost.siemensMaterialCost),
			outsideMaterialCost: roundHalfEven9(parentTotalCost.outsideMaterialCost + childTotalCost.outsideMaterialCost),
			mechanicalMaterialCost: roundHalfEven9(parentTotalCost.mechanicalMaterialCost + childTotalCost.mechanicalMaterialCost),
			electricalMaterialCost: roundHalfEven9(parentTotalCost.electricalMaterialCost + childTotalCost.electricalMaterialCost),
			actualTotalMaterialCost: roundHalfEven9(parentTotalCost.actualTotalMaterialCost + childTotalCost.actualTotalMaterialCost),
			actualSiemensMaterialCost: roundHalfEven9(parentTotalCost.actualSiemensMaterialCost + childTotalCost.actualSiemensMaterialCost),
			actualOutsideMaterialCost: roundHalfEven9(parentTotalCost.actualOutsideMaterialCost + childTotalCost.actualOutsideMaterialCost),
			actualMechanicalMaterialCost: roundHalfEven9(parentTotalCost.actualMechanicalMaterialCost + childTotalCost.actualMechanicalMaterialCost),
			actualElectricalMaterialCost: roundHalfEven9(parentTotalCost.actualElectricalMaterialCost + childTotalCost.actualElectricalMaterialCost)
		};
	}

	/**
	 * Adds two MiscCalculationResult objects together
	 */
	private addMiscResults(parentTotalCost: MiscCalculationResult, childTotalCost: MiscCalculationResult): MiscCalculationResult {
		return {
			totalMiscellaneousCost: roundHalfEven9(parentTotalCost.totalMiscellaneousCost + childTotalCost.totalMiscellaneousCost),
			totalNonRiskCost: roundHalfEven9(parentTotalCost.totalNonRiskCost + childTotalCost.totalNonRiskCost),
			totalRiskCost: roundHalfEven9(parentTotalCost.totalRiskCost + childTotalCost.totalRiskCost),
			totalSubcontractorCost: roundHalfEven9(parentTotalCost.totalSubcontractorCost + childTotalCost.totalSubcontractorCost)
		};
	}

	/**
	 * Interface to hold all cost types for a hierarchy level
	 */
	private createEmptyCosts(): HierarchyCosts {
		return {
			totalMaterialCosts: this.createEmptyMaterialResult(),
			totalLaborCosts: this.createEmptyLaborResult(),
			totalLaborBySalesOffice: new Map<number, LaborCalculationResult>(),
			totalMiscellaneousCost: this.createEmptyMiscResult()
		};
	}

	recalculateCostSummary(): void {
		this.getProjectData();
		if (this.commonDataService.isReadOnlyMode()) {
			this.loadExistingRates();
		}
		this.calculateHierarchyCosts();
	}

	async calculateCostSummary(): Promise<void> {
		try {
			this.getProjectData();
			if (this.commonDataService.isReadOnlyMode()) {
				// SAP Submittal: Use existing rates from metadata instead of fetching new ones
				this.loadExistingRates();
			} else {
				await this.getNewRates();
			}
			this.isEstimateOpenedCostCalculation = true;
			this.calculateHierarchyCosts();
			this.isEstimateOpenedCostCalculation = false;
			this.commonDataService.setProjectObjectDataAvailable(true);
		} catch (error) {
			console.error('Error calculating cost summary:', error);
		} finally {
			this.isEstimateOpenedCostCalculation = false;
		}
	}

	createRatesRequest() {
		const ratesRequest = new RatesRequestModel();
		ratesRequest.salesOfficeIds = this.laborMetadata.salesOfficeLaborRates.map(rate => rate.salesOfficeId);
		ratesRequest.bookingOfficeId = this.estimateData.salesOfficeId;
		ratesRequest.business = this.estimateData.business;
		ratesRequest.segmentId = this.estimateData.profitCenterId;
		ratesRequest.division = this.estimateMetaData.estimateSettings.businessUnitId;
		ratesRequest.defaultZineOffice = this.estimateMetaData.estimateSettings.defaultZineOffice;
		ratesRequest.defaultEngOffice = this.estimateMetaData.estimateSettings.defaultEngOffice;
		ratesRequest.cultureId = this.commonDataService.getEstimateCultureId();
		ratesRequest.subSegmentId = this.estimateData.subSegmentID;
		ratesRequest.selectedEngOfficeForECS = 0;
		ratesRequest.selectedDepOfficeForECS = 0;
		ratesRequest.isOldSapSubmitted = false;
		ratesRequest.isEcsApplicable = false;
		ratesRequest.pricePresentationData = this.commonDataService.getOtherMetaData()?.pricePresentationData;
		ratesRequest.useProjectLeadRate = this.estimateMetaData.estimateSettings.usePL;

		return ratesRequest;
	}

	private async getNewRates(): Promise<void> {
		const ratesRequest = this.createRatesRequest();

		const ratesResponse = await firstValueFrom(
			this.openEstimateApiService.getAllRates(ratesRequest).pipe(
				takeUntil(this.destroy$)
			)
		);

		if (!ratesResponse || !Array.isArray(ratesResponse.laborRates) ||
			!ratesResponse.laborConstants || !ratesResponse.taskBasedLaborConstants ||
			!ratesResponse.escalationModel || !ratesResponse.feesPercentages) {
			throw new Error('Invalid rates response received from the API.');
		}

		this.presentLaborRates = ratesResponse.laborRates;
		// Captured before synchronization, so this remains the pristine server calculation if the user accepts a sync.
		this.commonDataService.setDefaultLaborRates(ratesResponse.laborRates);
		this.laborConstants = ratesResponse.laborConstants;
		this.commonDataService.laborConstants = ratesResponse.laborConstants;
		this.commonDataService.taskBasedLaborConstants = ratesResponse.taskBasedLaborConstants;
		this.newEscalationRates = ratesResponse.escalationModel;
		this.feesData = ratesResponse.feesPercentages;
		this.commonDataService.setFeesData(this.feesData);
		await this.commonSynchronizationService.compareLaborRatesAndEscalationRates(ratesRequest, ratesResponse);
	}

	getProjectData(): void {
		this.project = this.commonDataService.getSQWebProject();
		this.laborMetadata = this.commonDataService.getLaborMetaData();
		this.estimateMetaData = this.commonDataService.getEstimateMetaData();
		this.miscMetaData = this.commonDataService.getMiscMetaData();
		this.estimateData = this.commonDataService.getEstimateData();
	}

	/**
	 * SAP Submittal: Loads existing rates from the already-cached LaborMetaData
	 * instead of fetching fresh rates from the API.
	 */
	private loadExistingRates(): void {
		this.presentLaborRates = this.laborMetadata?.salesOfficeLaborRates;
		this.laborConstants = this.laborMetadata?.laborConstants;
		this.feesData = this.commonDataService.getFeesData();
	}

	/**
	 * Calculates all costs (Material, Labor, Miscellaneous) for the entire project hierarchy.
	 * Each area's costs are set to the sum of all its children's costs.
	 * Recursively traverses all areas and their nested children.
	 */
	private calculateHierarchyCosts(): void {
		this.laborByLevelAndSalesOffice.clear();
		this.laborRatesIndex = null;
		this.zoneOfficeIdsCache.clear();
		this.estimateCommonFunctions.estimateNonTaxableItemsDictionary.clear();
		if (!this.project.isItemVisible) {
			// Project is hidden — set all costs to zero
			if (this.isEstimateOpenedCostCalculation) {
				for (const area of this.project.areas ?? []) {
					this.collectTypicalOfSystems(area);
				}
			}
			const zeroCosts = this.createEmptyMaterialResult();
			const zeroLabor = this.createEmptyLaborResult();
			const zeroMisc = this.createEmptyMiscResult();
			this.setActualMaterialCostsOnEstimateTab(zeroCosts);
			const emptyOfficeLaborMap = new Map<number, LaborCalculationResult>();
			this.setMaterialCostAtLevel(this.project, zeroCosts, zeroCosts);
			this.setLaborCostsAtLevel(this.project, zeroLabor, zeroLabor);
			this.setLaborBySalesOfficeAtLevel(this.project, emptyOfficeLaborMap, emptyOfficeLaborMap);
			this.setMiscellaneousCostsAtLevel(this.project, zeroMisc, zeroMisc);
			this.capturePreSubsidyMaterialCosts(this.project);
			this.lastProductivityReserve = CONSTANTS.ZERO;
			this.calculateFees(this.project, CONSTANTS.ZERO);
			this.flushSubContractorMapToEstimateTab();
			return;
		}

		// Individual costs specific to the project level (not from children)
		const projectIndividualOfficeLabor = new Map<number, LaborCalculationResult>();
		const individualLaborCosts = this.getLaborCostAtLevel(this.project, projectIndividualOfficeLabor);
		const individualMiscCosts = this.getMiscellaneousCostsAtLevel(this.project);

		const totalCosts = {
			totalMaterialCosts: this.createEmptyMaterialResult(),
			totalLaborCosts: individualLaborCosts,
			totalLaborBySalesOffice: this.cloneLaborBySalesOfficeMap(projectIndividualOfficeLabor),
			totalMiscellaneousCost: individualMiscCosts
		};

		if (!this.project.areas) {
			return;
		}
		this.proposalAreaList = [];
		let projectProductivityReserve = CONSTANTS.ZERO;
		for (const area of this.project.areas) {
			const childCosts = this.calculateAreaCosts(area);
			totalCosts.totalMaterialCosts = this.addMaterialResults(totalCosts.totalMaterialCosts, childCosts.totalMaterialCosts);
			totalCosts.totalLaborCosts = this.addLaborResults(totalCosts.totalLaborCosts, childCosts.totalLaborCosts);
			this.mergeLaborBySalesOfficeMaps(totalCosts.totalLaborBySalesOffice, childCosts.totalLaborBySalesOffice);
			totalCosts.totalMiscellaneousCost = this.addMiscResults(totalCosts.totalMiscellaneousCost, childCosts.totalMiscellaneousCost);
			projectProductivityReserve += area.productivityReserve ?? CONSTANTS.ZERO;
		}
		// This area list we only use for proposal generation advanced options, not used in calculations.
		this.commonDataService.proposalAdvancedOptionsAreaList = this.proposalAreaList;
		this.proposalAreaList = [];

		// Must run before setMaterialCostAtLevel: shipping & warranty read these values back from estimateTab.
		this.setActualMaterialCostsOnEstimateTab(totalCosts.totalMaterialCosts);

		// Project level has no individual material cost (only children contribute)
		this.setMaterialCostAtLevel(this.project, totalCosts.totalMaterialCosts, this.createEmptyMaterialResult());
		this.setLaborCostsAtLevel(this.project, totalCosts.totalLaborCosts, individualLaborCosts);
		this.setLaborBySalesOfficeAtLevel(this.project, totalCosts.totalLaborBySalesOffice, projectIndividualOfficeLabor);
		this.setMiscellaneousCostsAtLevel(this.project, totalCosts.totalMiscellaneousCost, individualMiscCosts);

		// Subsidy first: escalation is calculated on the subsidised material cost.
		this.capturePreSubsidyMaterialCosts(this.project);
		this.applySubsidy(this.project);

		this.applyEscalation(this.project);

		this.lastProductivityReserve = projectProductivityReserve;
		this.calculateFees(this.project, projectProductivityReserve);
		this.flushSubContractorMapToEstimateTab();
	}

	/**
	 * Recursively calculates all costs of an area as the sum of all its children's costs.
	 * @param area - The current area to process
	 * @returns The total costs of this area (sum of all children)
	 */
	private calculateAreaCosts(area: SQWebArea): HierarchyCosts {
		const totalCosts = this.createEmptyCosts();

		if (!area.isItemVisible) {
			area.productivityReserve = CONSTANTS.ZERO;
			// Hidden subtree contributes no cost, but its systems must still be registered
			// so consumers filtering on isItemVisible see the full typical-of group.
			if (this.isEstimateOpenedCostCalculation) {
				this.collectTypicalOfSystems(area);
			}
			return totalCosts;
		}

		this.proposalAreaList.push({ id: area.itemUniqueId, description: area.itemName });

		// Individual costs specific to this area level (not from children)
		const areaIndividualOfficeLabor = new Map<number, LaborCalculationResult>();
		const individualLaborCosts = this.getLaborCostAtLevel(area, areaIndividualOfficeLabor);
		const individualMiscCosts = this.getMiscellaneousCostsAtLevel(area);

		totalCosts.totalLaborCosts = individualLaborCosts;
		totalCosts.totalLaborBySalesOffice = this.cloneLaborBySalesOfficeMap(areaIndividualOfficeLabor);
		totalCosts.totalMiscellaneousCost = individualMiscCosts;

		// If no children, return the area's own costs
		if (!area.children || area.children.length === CONSTANTS.ZERO) {
			area.productivityReserve = CONSTANTS.ZERO;
			// Area has no individual material cost (only children contribute)
			this.setMaterialCostAtLevel(area, totalCosts.totalMaterialCosts, this.createEmptyMaterialResult());
			this.setLaborCostsAtLevel(area, totalCosts.totalLaborCosts, individualLaborCosts);
			this.setLaborBySalesOfficeAtLevel(area, totalCosts.totalLaborBySalesOffice, areaIndividualOfficeLabor);
			this.setMiscellaneousCostsAtLevel(area, totalCosts.totalMiscellaneousCost, individualMiscCosts);

			return totalCosts;
		}

		let productivityReserveSum = CONSTANTS.ZERO;

		for (const child of area.children) {
			if (this.isArea(child)) {
				// Recursively calculate nested area costs
				const childCosts = this.calculateAreaCosts(child);
				totalCosts.totalMaterialCosts = this.addMaterialResults(totalCosts.totalMaterialCosts, childCosts.totalMaterialCosts);
				totalCosts.totalLaborCosts = this.addLaborResults(totalCosts.totalLaborCosts, childCosts.totalLaborCosts);
				this.mergeLaborBySalesOfficeMaps(totalCosts.totalLaborBySalesOffice, childCosts.totalLaborBySalesOffice);
				totalCosts.totalMiscellaneousCost = this.addMiscResults(totalCosts.totalMiscellaneousCost, childCosts.totalMiscellaneousCost);
				productivityReserveSum += (child as SQWebArea).productivityReserve ?? CONSTANTS.ZERO;
			} else if (this.isSystem(child)) {
				// System has material, labor, and miscellaneous costs
				if (this.isEstimateOpenedCostCalculation) {
					this.addTypicalOfSystemInDictionary(child);
				}
				this.calculateSystemCosts(totalCosts, child);
			} else if (this.isMaterialNode(child)) {
				// Leaf nodes (Panel, Controller, DesigoOptic) only have material cost
				const nodeMaterialCosts = this.getMaterialCostAtLevel(child);
				totalCosts.totalMaterialCosts = this.addMaterialResults(totalCosts.totalMaterialCosts, nodeMaterialCosts);
				// Controllers contribute productivityReserve
				if (this.isController(child)) {
					productivityReserveSum += child.productivityReserve ?? CONSTANTS.ZERO;
				}
			}
		}

		area.productivityReserve = productivityReserveSum;

		// Set the area's costs: sumUp = total of all children + own, individual = own only
		// Area has no individual material cost (only children contribute)
		this.setMaterialCostAtLevel(area, totalCosts.totalMaterialCosts, this.createEmptyMaterialResult());
		this.setLaborCostsAtLevel(area, totalCosts.totalLaborCosts, individualLaborCosts);
		this.setLaborBySalesOfficeAtLevel(area, totalCosts.totalLaborBySalesOffice, areaIndividualOfficeLabor);
		this.setMiscellaneousCostsAtLevel(area, totalCosts.totalMiscellaneousCost, individualMiscCosts);

		return totalCosts;
	}


	/**
	 * Calculates and updates the total costs for a system, including material, labor, and miscellaneous costs.
	 *
	 * @param totalCosts - The object containing the accumulated hierarchy costs to be updated.
	 * @param child - The system for which the costs are being calculated.
	 *
	 * @remarks
	 * - Adds the material cost of the given system to the total material cost.
	 * - Retrieves and adds the labor cost of the system to the total labor costs.
	 * - Adds the miscellaneous cost of the system to the total miscellaneous cost.
	 * - Updates the labor cost on the system object.
	 * - Processes typical of system grouping
	 */
	private calculateSystemCosts(totalCosts: HierarchyCosts, system: SQWebSystem): void {
		if (!system.isItemVisible) {
			const emptyOfficeLaborMap = new Map<number, LaborCalculationResult>();
			this.setLaborBySalesOfficeAtLevel(system, emptyOfficeLaborMap, emptyOfficeLaborMap);
			return;
		}

		const systemMaterialCosts = this.getMaterialCostAtLevel(system);

		const systemOfficeLabor = new Map<number, LaborCalculationResult>();
		const systemLaborCosts = this.getLaborCostAtLevel(system, systemOfficeLabor);
		const systemMiscCosts = this.getMiscellaneousCostsAtLevel(system);

		totalCosts.totalMaterialCosts = this.addMaterialResults(totalCosts.totalMaterialCosts, systemMaterialCosts);
		totalCosts.totalLaborCosts = this.addLaborResults(totalCosts.totalLaborCosts, systemLaborCosts);
		this.mergeLaborBySalesOfficeMaps(totalCosts.totalLaborBySalesOffice, systemOfficeLabor);
		totalCosts.totalMiscellaneousCost = this.addMiscResults(totalCosts.totalMiscellaneousCost, systemMiscCosts);

		// For systems (leaf level), individual cost equals the sumUp cost
		this.setMaterialCostAtLevel(system, systemMaterialCosts, systemMaterialCosts);
		this.setLaborCostsAtLevel(system, systemLaborCosts, systemLaborCosts);
		this.setLaborBySalesOfficeAtLevel(system, systemOfficeLabor, systemOfficeLabor);
		this.setMiscellaneousCostsAtLevel(system, systemMiscCosts, systemMiscCosts);
	}

	private addTypicalOfSystemInDictionary(system: SQWebSystem) {
		const helperId = system?.systemTypicalOfHelperUniqueId;
		if (!helperId) {
			return;
		}
		const dictionaryItem = this.commonDataService.getTypicalOfHelpers()[helperId];
		if (!dictionaryItem) {
			return;
		}
		dictionaryItem.typicalOfSystems ??= [];
		dictionaryItem.typicalOfSystems.push(system);
	}

	/**
	 * Registers every system under a hidden area (including nested areas) in the typical-of dictionary.
	 * Only invoked for subtrees the cost walk skips, so each node is still visited exactly once overall.
	 */
	private collectTypicalOfSystems(area: SQWebArea): void {
		for (const child of area.children ?? []) {
			if (this.isArea(child)) {
				this.collectTypicalOfSystems(child);
			} else if (this.isSystem(child)) {
				this.addTypicalOfSystemInDictionary(child);
			}
		}
	}

	private calculateFees(project: SQWebProject, projectProductivityReserve: number) {
		// Ensure category objects exist before setting costs
		project.travelExpensesCategory = project.travelExpensesCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.shippingCategory = project.shippingCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.warrantyCategory = project.warrantyCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.taxCategory = project.taxCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.subcontractorFeeCategory = project.subcontractorFeeCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.productivityReserveCategory = project.productivityReserveCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.autoRiskCategory = project.autoRiskCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };
		project.mechanicalFeeCategory = project.mechanicalFeeCategory ?? { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO };

		project.travelExpensesCategory.sumUpCost = this.getTravelExpenseCost();
		project.shippingCategory.sumUpCost = this.getShippingCost(project);
		project.warrantyCategory.sumUpCost = this.getWarrantyCost(project);
		project.taxCategory.sumUpCost = this.getTaxCost(project);
		project.subcontractorFeeCategory.sumUpCost = this.getSubcontractorFeeCostAtLevel(project);
		project.productivityReserveCategory.sumUpCost = this.getProductivityReserveCost(projectProductivityReserve);
		project.autoRiskCategory.sumUpCost = this.getAutoRiskCostAtLevel(project);
		project.mechanicalFeeCategory.sumUpCost = this.getMechanicalFeeCost(project);
	}

	/**
	 * Converts the accumulated subcontractor map into the estimateTab array.
	 * Called at the end of calculateHierarchyCosts after all levels have been processed.
	 */
	private flushSubContractorMapToEstimateTab(): void {
		const estimateTab = this.estimateMetaData?.estimateTab;
		if (!estimateTab) {
			return;
		}
		// Misc data only holds the values as of the last save, so keep the user's unsaved edits from the previous array.
		const previousByKey = new Map<string, Subcontractors>();
		for (const previous of estimateTab.estimateTabSubContractors ?? []) {
			previousByKey.set(previous.referenceNumber ?? previous.description ?? '', previous);
		}
		if (previousByKey.size > CONSTANTS.ZERO) {
			this.retainedSubContractorState = previousByKey;
		}
		for (const [key, current] of this.estimateSubContractorMap) {
			const previous = previousByKey.get(key) ?? this.retainedSubContractorState.get(key);
			if (previous) {
				current.sellPrice = previous.sellPrice ?? current.sellPrice;
				current.gmPercent = previous.gmPercent ?? current.gmPercent;
				current.isLastModifiedSubContSellPrice = previous.isLastModifiedSubContSellPrice ?? current.isLastModifiedSubContSellPrice;
			}
		}
		estimateTab.estimateTabSubContractors = Array.from(this.estimateSubContractorMap.values());
		this.estimateSubContractorMap.clear();
	}

	/**
	 * Accumulates a subcontractor item into the map, merging by referenceNumber.
	 * Same referenceNumber across project/area/system levels results in one row with summed cost.
	 * Handles typical-of multiplication implicitly since getMiscellaneousCostsAtLevel
	 * is called once per system in the typical-of group.
	 */
	private collectSubContractorItem(sub: Subcontractors): void {
		const key = this.estimateCommonFunctions.getSubContractorKey(sub);
		const existing = this.estimateSubContractorMap.get(key);
		if (existing) {
			existing.cost += sub.cost;
			// existing.sellPrice += sub.sellPrice;
		} else if (this.estimateCommonFunctions.isUserDefinedSubContractor(sub)) {
			this.estimateSubContractorMap.set(key, {
				...sub,
				referenceNumber: key,
				description: CONSTANTS.USER_DEFINED_SUBCONTRACTOR_DESCRIPTION,
				guid: CONSTANTS.EMPTY_STRING
			});
		} else {
			this.estimateSubContractorMap.set(key, { ...sub });
		}
	}

	/**
	 * Gets the material cost from a hierarchy item, broken down by material category.
	 * Iterates through systemChildrenCollection which can contain Material or SubSystem items.
	 * SubSystem items are recursed into to categorize their child materials.
	 */
	private getMaterialCostAtLevel(item: IAreaChild): MaterialCalculationResult {
		const result = this.createEmptyMaterialResult();
		const system = item as SQWebSystem;
		const typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
		let materialData: MaterialData;
		if (system.systemTypicalOfHelperUniqueId) {
			const systemTypicalOfHelper = typicalOfHelper[system.systemTypicalOfHelperUniqueId];
			materialData = systemTypicalOfHelper?.typicalOfCollectionHolderId ? this.materialLevelwiseData?.materialsCollection[systemTypicalOfHelper.typicalOfCollectionHolderId] : null;
		} else {
			materialData = this.materialLevelwiseData?.materialsCollection ? this.materialLevelwiseData.materialsCollection[item.itemUniqueId] : null;
		}
		if (!materialData?.systemChildrenCollection) {
			return result;
		}
		this.categorizeMaterialChildren(materialData.systemChildrenCollection.filter(x => x.isMaster), result, CONSTANTS.ONE);
		return result;
	}

	/**
	 * Recursively categorizes material costs from a collection of system components.
	 * Materials are categorized by their materialType; SubSystems are recursed into.
	 * @param children - The collection of system components (Material or SubSystem)
	 * @param result - The MaterialCalculationResult to accumulate costs into
	 */
	private categorizeMaterialChildren(children: SystemComponent[], result: MaterialCalculationResult, subSystemQuantity: number): void {
		for (const child of children) {
			const materialChild = child as Material;
			const subSystemChild = child as SubSystem;

			if (materialChild.materialType) {
				// Direct Material - categorize by materialType
				const cost = materialChild.cost ?? CONSTANTS.ZERO;
				const qty = (materialChild.quantity ?? CONSTANTS.ZERO) * subSystemQuantity;
				const actualUnitCost = this.getActualMaterialUnitCost(materialChild);
				result.totalMaterialCost += roundHalfEven9(cost * qty);
				result.actualTotalMaterialCost += roundHalfEven9(actualUnitCost * qty);
				this.addCostToMaterialCategory(result, materialChild.materialType, cost, actualUnitCost, qty);
			} else if (subSystemChild.children?.length > CONSTANTS.ZERO) {
				// SubSystem - recurse into its children
				this.categorizeMaterialChildren(subSystemChild.children.filter(x => x.isMaster), result, subSystemChild.quantity);
			} else {
				// Fallback: unknown component type, add cost to total only
				console.warn(`Unknown component type for child with ID ${child.description}. Adding cost to total without categorization.`);
				result.totalMaterialCost += child.cost ?? CONSTANTS.ZERO;
				result.actualTotalMaterialCost += child.cost ?? CONSTANTS.ZERO;
			}
		}
	}

	/**
	 * Returns a material's unit cost before any product promotion discount.
	 *
	 * `Material.cost` is persisted net of the promotion (desktop convention: `IsPPA` marks such
	 * materials and the material checker refuses to overwrite their cost), so the promotion has to
	 * be reversed here. The result is only ever accumulated in memory - it is never written back -
	 * so no rounding drift accumulates across apply/remove cycles.
	 * A promotion matches when its `affectedMaterial` equals the material's `ppType`, the same join
	 * the GIL export uses.
	 */
	private getActualMaterialUnitCost(material: Material): number {
		const cost = material.cost ?? CONSTANTS.ZERO;
		if (!material.isPPA || material.ppType === CONSTANTS.EMPTY_PP_TYPE) {
			return cost;
		}
		const promotions = this.estimateMetaData?.estimateSettings?.productPromotionList;
		const discount = promotions?.find(promotion => promotion.affectedMaterial === material.ppType)?.discount ?? CONSTANTS.ZERO;
		if (discount <= CONSTANTS.ZERO || discount >= CONSTANTS.HUNDRED) {
			return cost;
		}
		return cost / (CONSTANTS.ONE - (discount / CONSTANTS.HUNDRED));
	}

	/**
	 * Maps a material's cost to the appropriate category based on its materialType string.
	 * Supports both English and French material type values.
	 * Accumulates the promotion-discounted cost and the actual (pre-promotion) cost separately.
	 */
	private addCostToMaterialCategory(result: MaterialCalculationResult, materialType: string, cost: number, actualUnitCost: number, qty: number): void {
		const discountedCost = roundHalfEven9(cost * qty);
		const actualCost = roundHalfEven9(actualUnitCost * qty);

		if (MATERIAL_TYPE_SIEMENS.has(materialType) || MATERIAL_AT_TYPE_SIEMENS.has(materialType)) {
			// Special case: SI OEM Material is categorized under Siemens Material when not in PPM
			//TO-DO: Add check for PPM mode when implemented
			result.siemensMaterialCost += discountedCost;
			result.actualSiemensMaterialCost += actualCost;
		} else if (MATERIAL_TYPE_OUTSIDE.has(materialType)) {
			result.outsideMaterialCost += discountedCost;
			result.actualOutsideMaterialCost += actualCost;
		} else if (MATERIAL_TYPE_MECHANICAL.has(materialType)) {
			result.mechanicalMaterialCost += discountedCost;
			result.actualMechanicalMaterialCost += actualCost;
		} else if (MATERIAL_TYPE_ELECTRICAL.has(materialType)) {
			result.electricalMaterialCost += discountedCost;
			result.actualElectricalMaterialCost += actualCost;
		}
	}

	/**
	 * Gets the labor cost from a hierarchy item
	 */
	private getLaborCostAtLevel(
		item: IAreaChild | SQWebArea | SQWebProject,
		officeLaborMap?: Map<number, LaborCalculationResult>
	): LaborCalculationResult {
		let laborCostResult: LaborCalculationResult = this.createEmptyLaborResult();
		const targetOfficeLaborMap = officeLaborMap ?? new Map<number, LaborCalculationResult>();
		// We will not use the labor rates from presentLaborRates collection directly as this are the labor rates that are 
		// fetched from DB directly, instead we need to use the labor rates of the estimate itself so that the values set in 
		// desktop persist and even if the labor rates change at the server, the estimate labor rates are updated in the 
		// commonDataService while opening the estimate.
		const laborRates = this.commonDataService.getLaborMetaData()?.salesOfficeLaborRates;

		if (!laborRates) {
			return laborCostResult;
		}

		const laborsCollection = this.laborLevelwiseData?.laborsCollection;
		let laborLookupId = item.itemUniqueId;
		const system = item as SQWebSystem;
		const typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
		if (system.systemTypicalOfHelperUniqueId) {
			const systemTypicalOfHelper = typicalOfHelper[system.systemTypicalOfHelperUniqueId];
			laborLookupId = systemTypicalOfHelper?.typicalOfCollectionHolderId ?? item.itemUniqueId;
		}
		const laborData = laborsCollection && laborLookupId in laborsCollection
			? laborsCollection[laborLookupId]
			: null;
		if (laborData) {
			//TO-DO: PPM Mode Check
			// One time loop-up for the fetching the zone offices and the IsECS flag and being passed to the getCommonLaborCostAtLevel method as a 
			// parameter instead of doing this in the method itself so that we dont compromise the performance by repeatedly fetching this data inside the loop.
			const metaData = this.commonMetaDataService.getMetaData()?.allZoneOfficeIds;
			const ocOfficeIds = this.parseZoneOfficeIds(metaData.oc);
			const zineOfficeIds = this.parseZoneOfficeIds(metaData.zine);
			const isECS = this.estimateData.isECS;

			const otPwrCollection = laborData.stdSalesOfficesOtPwrAdjustmentsCollection;
			otPwrCollection?.forEach(otPwrItem => {
				const laborRate = this.findLaborRate(laborRates, otPwrItem.salesOfficeId);
				if (laborRate) {
					const overtimeRates = laborRate.stdOverTimeRates;
					const contribution = this.createEmptyLaborResult();
					this.getCommonLaborCostAtLevel(contribution, otPwrItem, overtimeRates, laborRate, ocOfficeIds, zineOfficeIds, isECS, laborRates);
					this.setTotalLaborCost(contribution);
					laborCostResult = this.addLaborResults(laborCostResult, contribution);
					this.addOfficeLaborContribution(targetOfficeLaborMap, otPwrItem.salesOfficeId, contribution);
				}
			});

			// Check if item is a DXR system (only applicable for IAreaChild nodes).
			// DXR OT/PWR collection only applies to DXR master systems, not DXR children or non-DXR systems
			const isDXRSystem = this.isAreaChild(item) && this.isSystem(item) && item.isSystemDXR && item.isMaster;
			if (isDXRSystem) {
				//TO-DO: PPM Mode Check
				const dxrOtPwrCollection = laborData.stdSalesOfficesOtPwrAdjustmentsDXRCollection;

				dxrOtPwrCollection?.forEach(otPwrItem => {
					const laborRate = this.findLaborRate(laborRates, otPwrItem.salesOfficeId);
					if (laborRate) {
						const overtimeRates = laborRate.stdOverTimeRates;
						const contribution = this.createEmptyLaborResult();
						this.getCommonLaborCostAtLevel(contribution, otPwrItem, overtimeRates, laborRate, ocOfficeIds, zineOfficeIds, isECS, laborRates);
						this.setTotalLaborCost(contribution);
						laborCostResult = this.addLaborResults(laborCostResult, contribution);
						this.addOfficeLaborContribution(targetOfficeLaborMap, otPwrItem.salesOfficeId, contribution);
					}
				});
			}

			this.setTotalLaborCost(laborCostResult);
		}

		return laborCostResult;
	}

	/** Callers only read the returned array; it is shared for the duration of a run. */
	private parseZoneOfficeIds(ids: string | null | undefined): number[] {
		if (!ids) return [];
		let parsed = this.zoneOfficeIdsCache.get(ids);
		if (!parsed) {
			parsed = ids.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
			this.zoneOfficeIdsCache.set(ids, parsed);
		}
		return parsed;
	}

	/** First rate for the sales office, same result as Array.find but O(1) per lookup after the first call of a run. */
	private findLaborRate(laborRates: LaborRates[], salesOfficeId: number): LaborRates | undefined {
		if (this.laborRatesIndex?.source !== laborRates) {
			const byOffice = new Map<number, LaborRates>();
			for (const rate of laborRates) {
				if (!byOffice.has(rate.salesOfficeId)) {
					byOffice.set(rate.salesOfficeId, rate);
				}
			}
			this.laborRatesIndex = { source: laborRates, byOffice };
		}
		return this.laborRatesIndex.byOffice.get(salesOfficeId);
	}

	private getCommonLaborCostAtLevel(laborCostResult: LaborCalculationResult, otPwrItem: OtPwrSalesOffices,
		overtimeRates: OverTimeRates, laborRate: LaborRates, ocOfficeIds: number[], zineOfficeIds: number[],
		isECS: boolean, laborRatesList: LaborRates[]) {
		//To-Do: Add the check for PL Rates when implemented

		const pm = this.getProjectManagementLabor(otPwrItem, overtimeRates, laborRate.projectManagementDbRate);
		laborCostResult.pmLaborCost += pm.cost;
		laborCostResult.pmHours += pm.hours;

		const mech = this.getMechanicalLabor(otPwrItem, overtimeRates, laborRate.mechanicalRates, laborRate.adjMechPWRRate);
		laborCostResult.mechanicalLaborCost += mech.cost;
		laborCostResult.mechanicalHours += mech.hours;

		const elec = this.getElectricalLabor(otPwrItem, overtimeRates, laborRate.electricianRates, laborRate.adjElecPWRRate);
		laborCostResult.electricalLaborCost += elec.cost;
		laborCostResult.electricalHours += elec.hours;

		const eng = this.getEngineeringLabor(otPwrItem, overtimeRates, laborRate.engineeringRates);
		laborCostResult.engineeringLaborCost += eng.cost;
		laborCostResult.engineeringHours += eng.hours;

		const spec = this.getSpecialistLabor(otPwrItem, overtimeRates, laborRate.specialistRates, laborRate.adjSpecPWRRate);
		laborCostResult.specialistLaborCost += spec.cost;
		laborCostResult.specialistHours += spec.hours;

		const neng = this.getNetworkEngineeringLabor(otPwrItem, overtimeRates, laborRate.networkEngineeringRates);
		laborCostResult.networkEngineeringLaborCost += neng.cost;
		laborCostResult.networkEngineeringHours += neng.hours;

		const qualEng = this.getQualificationEngineeringLabor(otPwrItem, overtimeRates, laborRate.qualificationEngineeringRates);
		laborCostResult.qualificationEngineeringLaborCost += qualEng.cost;
		laborCostResult.qualificationEngineeringHours += qualEng.hours;

		const cyberSec = this.getCyberSecEngineeringLabor(otPwrItem, overtimeRates, laborRate.cyberSecEngineeringRates);
		laborCostResult.cyberSecEngineeringLaborCost += cyberSec.cost;
		laborCostResult.cyberSecEngineeringHours += cyberSec.hours;

		const energyEng = this.getEnergyEngineeringLabor(otPwrItem, overtimeRates, laborRate.energyEngineeringRates);
		laborCostResult.energyEngineeringLaborCost += energyEng.cost;
		laborCostResult.energyEngineeringHours += energyEng.hours;

		const leng = this.getLENGEngineeringLabor(otPwrItem, overtimeRates, laborRate.lEngineeringRates);
		laborCostResult.lEngineeringLaborCost += leng.cost;
		laborCostResult.lEngineeringHours += leng.hours;

		// handle prog and graphics labor calculations in the below if block only if the sales office is default OC office or if the sales office is one of the OC offices (in case of ECS = true).
		if (otPwrItem.salesOfficeId == this.estimateMetaData.estimateSettings.defaultEngOffice ||
			ocOfficeIds.includes(otPwrItem.salesOfficeId)) {
			let laborRateOCOffice: LaborRates | null = null;
			// Incase the estimate is an ECS estimate, use the sales office ID of the OT/PWR record being traversed; otherwise, use the default OC office ID.
			const selectedOfficeId = isECS
				? otPwrItem.salesOfficeId
				: this.estimateMetaData.estimateSettings.defaultEngOffice;

			laborRateOCOffice = (laborRatesList ? this.findLaborRate(laborRatesList, selectedOfficeId) : undefined) ?? null;

			const programming = this.getProgrammingLabor(otPwrItem, laborRateOCOffice);
			laborCostResult.programmingLaborCost += programming.cost;
			laborCostResult.programmingHours += programming.hours;

			const graphics = this.getGraphicsLabor(otPwrItem, laborRateOCOffice);
			laborCostResult.graphicsLaborCost += graphics.cost;
			laborCostResult.graphicsHours += graphics.hours;
		}
		// handle DDS labor calculations in the below if block only if the sales office is default ZINE office or if the sales office is one of the ZINE offices (in case of ECS = true).
		else if (otPwrItem.salesOfficeId == this.estimateMetaData.estimateSettings.defaultZineOffice ||
			zineOfficeIds.includes(otPwrItem.salesOfficeId)) {
			let laborRateZineOffice: LaborRates | null = null;
			// Incase the estimate is an ECS estimate, use the sales office ID of the OT/PWR record being traversed; otherwise, use the default Zine office ID.
			const selectedOfficeId = isECS
				? otPwrItem.salesOfficeId
				: this.estimateMetaData.estimateSettings.defaultZineOffice;

			laborRateZineOffice = (laborRatesList ? this.findLaborRate(laborRatesList, selectedOfficeId) : undefined) ?? null;

			const dds = this.getDigitalDeploymentLabor(otPwrItem, laborRateZineOffice);
			laborCostResult.digitalDeploymentLaborCost += dds.cost;
			laborCostResult.digitalDeploymentHours += dds.hours;
		}
		else {
			// For sonar compliance
		}
	}

	/**
	 * Gets the PM labor cost and hours
	 */
	private getProjectManagementLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, pmRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].projectManagementLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].projectManagementLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].projectManagementLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].projectManagementLabor);

		const cost = roundHalfEven9(otHours * pmRate +
			ot11xHours * overtimeRates.overTime11X.overtimePMRate +
			ot15xHours * overtimeRates.overTime15X.overtimePMRate +
			ot2xHours * overtimeRates.overTime2X.overtimePMRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Programming labor cost and hours
	 */
	private getProgrammingLabor(otPwrItem: OtPwrSalesOffices, laborRateOCOffice: LaborRates): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].programmingLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].programmingLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].programmingLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].programmingLabor);

		let cost = CONSTANTS.ZERO;
		if (laborRateOCOffice != null) {
			cost = roundHalfEven9(otHours * laborRateOCOffice.programmingRates +
				ot11xHours * laborRateOCOffice.stdOverTimeRates.overTime11X.overtimeProgRate +
				ot15xHours * laborRateOCOffice.stdOverTimeRates.overTime15X.overtimeProgRate +
				ot2xHours * laborRateOCOffice.stdOverTimeRates.overTime2X.overtimeProgRate);
		}

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Graphics labor cost and hours
	 */
	private getGraphicsLabor(otPwrItem: OtPwrSalesOffices, laborRateOCOffice: LaborRates): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].graphicsLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].graphicsLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].graphicsLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].graphicsLabor);

		let cost = CONSTANTS.ZERO;
		if (laborRateOCOffice != null) {
			cost = roundHalfEven9(otHours * laborRateOCOffice.graphicsRates +
				ot11xHours * laborRateOCOffice.stdOverTimeRates.overTime11X.overtimeGrapRate +
				ot15xHours * laborRateOCOffice.stdOverTimeRates.overTime15X.overtimeGrapRate +
				ot2xHours * laborRateOCOffice.stdOverTimeRates.overTime2X.overtimeGrapRate);
		}

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Digital Deployment labor cost and hours
	 */
	private getDigitalDeploymentLabor(otPwrItem: OtPwrSalesOffices, laborRateZineOffice: LaborRates): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].digitalDeploymentLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].digitalDeploymentLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].digitalDeploymentLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].digitalDeploymentLabor);

		let cost = CONSTANTS.ZERO;
		if (laborRateZineOffice != null) {
			cost = roundHalfEven9(otHours * laborRateZineOffice.ddsRates +
				ot11xHours * laborRateZineOffice.stdOverTimeRates.overTime11X.overtimeDdsRate +
				ot15xHours * laborRateZineOffice.stdOverTimeRates.overTime15X.overtimeDdsRate +
				ot2xHours * laborRateZineOffice.stdOverTimeRates.overTime2X.overtimeDdsRate);
		}

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Engineering labor cost and hours
	 */
	private getEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, engRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].engineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].engineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].engineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].engineerLabor);

		const cost = roundHalfEven9(otHours * engRate +
			ot11xHours * overtimeRates.overTime11X.overtimeENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Specialist labor cost and hours
	 */
	private getSpecialistLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, specRate: number, adjustmentPWRRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].specialistLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].specialistLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].specialistLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].specialistLabor);
		const pwrHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR].specialistLabor);
		const pwr11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_11X].specialistLabor);
		const pwr15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_15X].specialistLabor);
		const pwr2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_2X].specialistLabor);

		const cost = roundHalfEven9(otHours * specRate +
			ot11xHours * overtimeRates.overTime11X.overtimeSPECRate +
			ot15xHours * overtimeRates.overTime15X.overtimeSPECRate +
			ot2xHours * overtimeRates.overTime2X.overtimeSPECRate +
			//Adding PWR Rates
			pwrHours * adjustmentPWRRate +
			pwr11xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwrShiftDifferential11X +
			pwr15xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR15X +
			pwr2xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR2X);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours +
			pwrHours + pwr11xHours + pwr15xHours + pwr2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Mechanical labor cost and hours
	 */
	private getMechanicalLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, mechRate: number, adjustmentPWRRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].mechanicalEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].mechanicalEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].mechanicalEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].mechanicalEngineerLabor);
		const pwrHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR].mechanicalEngineerLabor);
		const pwr11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_11X].mechanicalEngineerLabor);
		const pwr15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_15X].mechanicalEngineerLabor);
		const pwr2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_2X].mechanicalEngineerLabor);

		const cost = roundHalfEven9(otHours * mechRate +
			ot11xHours * overtimeRates.overTime11X.overtimeMECHRate +
			ot15xHours * overtimeRates.overTime15X.overtimeMECHRate +
			ot2xHours * overtimeRates.overTime2X.overtimeMECHRate +
			//Adding PWR Rates
			pwrHours * adjustmentPWRRate +
			pwr11xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwrShiftDifferential11X +
			pwr15xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR15X +
			pwr2xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR2X);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours +
			pwrHours + pwr11xHours + pwr15xHours + pwr2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Electrical labor cost and hours
	 */
	private getElectricalLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, elecRate: number, adjustmentPWRRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].electricalEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].electricalEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].electricalEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].electricalEngineerLabor);
		const pwrHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR].electricalEngineerLabor);
		const pwr11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_11X].electricalEngineerLabor);
		const pwr15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_15X].electricalEngineerLabor);
		const pwr2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.PWR_2X].electricalEngineerLabor);

		const cost = roundHalfEven9(otHours * elecRate +
			ot11xHours * overtimeRates.overTime11X.overtimeELECRate +
			ot15xHours * overtimeRates.overTime15X.overtimeELECRate +
			ot2xHours * overtimeRates.overTime2X.overtimeELECRate +
			//Adding PWR Rates
			pwrHours * adjustmentPWRRate +
			pwr11xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwrShiftDifferential11X +
			pwr15xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR15X +
			pwr2xHours * adjustmentPWRRate * this.laborMetadata.laborConstants.pwR2X);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours +
			pwrHours + pwr11xHours + pwr15xHours + pwr2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Network Engineering labor cost and hours
	 */
	private getNetworkEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, nengRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].networkEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].networkEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].networkEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].networkEngineerLabor);

		const cost = roundHalfEven9(otHours * nengRate +
			ot11xHours * overtimeRates.overTime11X.overtimeNENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeNENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeNENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Qualification Engineer labor cost and hours
	 */
	private getQualificationEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, qualEngRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].qualificationEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].qualificationEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].qualificationEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].qualificationEngineerLabor);

		const cost = roundHalfEven9(otHours * qualEngRate +
			ot11xHours * overtimeRates.overTime11X.overtimeQENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeQENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeQENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Cyber Security Engineer labor cost and hours
	 */
	private getCyberSecEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, cyberSecRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].cyberSecEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].cyberSecEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].cyberSecEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].cyberSecEngineerLabor);

		const cost = roundHalfEven9(otHours * cyberSecRate +
			ot11xHours * overtimeRates.overTime11X.overtimeCENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeCENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeCENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the Energy Engineer labor cost and hours
	 */
	private getEnergyEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, energyEngRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].energyEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].energyEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].energyEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].energyEngineerLabor);

		const cost = roundHalfEven9(otHours * energyEngRate +
			ot11xHours * overtimeRates.overTime11X.overtimeEENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeEENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeEENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the LENG Engineer labor cost and hours
	 */
	private getLENGEngineeringLabor(otPwrItem: OtPwrSalesOffices, overtimeRates: OverTimeRates, lengEngRate: number): { cost: number; hours: number } {
		const otHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT].lengEngineerLabor);
		const ot11xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_11X].lengEngineerLabor);
		const ot15xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_15X].lengEngineerLabor);
		const ot2xHours = this.getNACheckedLaborValue(otPwrItem.overTimePWRAdjustments[CONSTANTS.OT_2X].lengEngineerLabor);

		const cost = roundHalfEven9(otHours * lengEngRate +
			ot11xHours * overtimeRates.overTime11X.overtimeLENGRate +
			ot15xHours * overtimeRates.overTime15X.overtimeLENGRate +
			ot2xHours * overtimeRates.overTime2X.overtimeLENGRate);

		const hours = roundHalfEven9(otHours + ot11xHours + ot15xHours + ot2xHours);
		return { cost, hours };
	}

	/**
	 * Gets the miscellaneous cost from a hierarchy item
	 */
	private getMiscellaneousCostsAtLevel(item: IHierarchyItem): MiscCalculationResult {
		let nonRiskCost = CONSTANTS.ZERO;
		let riskCost = CONSTANTS.ZERO;
		let totalSubcontractorCost = CONSTANTS.ZERO;

		const miscsCollection = this.miscLevelwiseData?.miscsCollection;
		let miscLookupId = item.itemUniqueId;
		const system = item as SQWebSystem;
		const typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
		if (system.systemTypicalOfHelperUniqueId) {
			const systemTypicalOfHelper = typicalOfHelper[system.systemTypicalOfHelperUniqueId];
			miscLookupId = systemTypicalOfHelper?.typicalOfCollectionHolderId ?? item.itemUniqueId;
		}
		const miscData = miscsCollection && miscLookupId in miscsCollection
			? miscsCollection[miscLookupId]
			: null;
		if (miscData) {
			miscData.miscellaneousCollection?.forEach(misc => {
				if (CONSTANTS.RISK_ITEMS.includes(misc.referenceNumber)) {
					riskCost += misc.cost;
				} else if (CONSTANTS.NON_TAXABLE_ITEMS.includes(misc.referenceNumber)) {
					const existingItem = this.estimateCommonFunctions.estimateNonTaxableItemsDictionary.get(misc.referenceNumber);
					if (existingItem) {
						existingItem.cost += misc.cost;
					} else {
						this.estimateCommonFunctions.estimateNonTaxableItemsDictionary.set(misc.referenceNumber, {
							referenceNumber: misc.referenceNumber,
							cost: misc.cost,
							sellPrice: CONSTANTS.ZERO
						});
					}
					nonRiskCost += misc.cost;
				} else {
					nonRiskCost += misc.cost;
				}
			}
			);

			miscData.subcontractorCollection?.forEach(sub => {
				totalSubcontractorCost += sub.cost;
				this.collectSubContractorItem(sub);
			});
		}
		return {
			totalMiscellaneousCost: roundHalfEven9(nonRiskCost + riskCost),
			totalNonRiskCost: roundHalfEven9(nonRiskCost),
			totalRiskCost: roundHalfEven9(riskCost),
			totalSubcontractorCost: roundHalfEven9(totalSubcontractorCost)
		};
	}

	private getSubcontractorFeeCostAtLevel(item: SQWebProject): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.subcontractorFeeCost ?? CONSTANTS.ZERO;
		}
		return this.estimateCommonFunctions.calculateSubcontractorFeeCost(
			item.subcontractorCategory.sumUpCost,
			this.estimateMetaData.estimateSettings.useSubFee,
			this.feesData.subcontractorFeePercentage,
			this.feesData.subcontractorFeeThresholdCost
		);
	}

	private getAutoRiskCostAtLevel(item: SQWebProject): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.autoRiskCost ?? CONSTANTS.ZERO;
		}
		const costInputs: DynamicFeeCostInputs = {
			materialCost: item.totalMaterialCategory.sumUpCost,
			laborCost: item.laborTotalCategory.sumUpCost,
			miscCost: item.miscellaneousCategory.sumUpCost,
			subcontractorCost: item.subcontractorCategory.sumUpCost,
			travelCost: item.travelExpensesCategory.sumUpCost,
			subcontractorFeeCost: item.subcontractorFeeCategory.sumUpCost,
			productivityReserveCost: item.productivityReserveCategory.sumUpCost,
		};
		return this.estimateCommonFunctions.calculateAutoRiskCost(
			costInputs,
			this.estimateMetaData.estimateSettings.useAutoRisk,
			this.estimateMetaData.estimateSettings.autoRiskPercentage
		);
	}

	private getMechanicalFeeCost(item: SQWebProject): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.mechFeeCost ?? CONSTANTS.ZERO;
		}
		const costInputs: DynamicFeeCostInputs = {
			materialCost: item.totalMaterialCategory.sumUpCost,
			laborCost: item.laborTotalCategory.sumUpCost,
			miscCost: item.miscellaneousCategory.sumUpCost,
			subcontractorCost: item.subcontractorCategory.sumUpCost,
			travelCost: item.travelExpensesCategory.sumUpCost,
			subcontractorFeeCost: item.subcontractorFeeCategory.sumUpCost,
			productivityReserveCost: item.productivityReserveCategory.sumUpCost,
		};
		return this.estimateCommonFunctions.calculateMechanicalFeeCost(
			costInputs,
			item.autoRiskCategory.sumUpCost,
			this.estimateMetaData.estimateSettings.useMechFee,
			this.feesData.mechanicalFeePercentage
		);
	}

	private getTravelExpenseCost(): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.travelExpenseCost ?? CONSTANTS.ZERO;
		}
		if (!this.miscMetaData) {
			return CONSTANTS.ZERO;
		}

		this.rebuildTravelExpensesFromLaborHours();

		// Overridden per-diem/lodging days survive the rebuild, so a hidden project must be zeroed explicitly.
		if (!this.project.isItemVisible) {
			return CONSTANTS.ZERO;
		}

		const travelExpenses = this.miscMetaData.travelExpensesCollection;
		const travelRates = this.miscMetaData.travelRates;

		const totalTravelExpense = travelExpenses.reduce((total, expense) =>
			total
			+ this.calculateIndividualTravelCost(expense.pmExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.engExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.specExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.mechExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.elecExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.eengExpenses, travelRates)
			+ this.calculateIndividualTravelCost(expense.lengExpenses, travelRates)
			, CONSTANTS.ZERO);
		return totalTravelExpense;
	}

	private rebuildTravelExpensesFromLaborHours(): void {
		if (!this.miscMetaData || !this.project) {
			return;
		}

		const travelRates = this.miscMetaData.travelRates;
		if (!travelRates) {
			return;
		}

		const projectLevelLaborData = this.laborByLevelAndSalesOffice.get(this.project.itemUniqueId);
		const projectOfficeHours = projectLevelLaborData?.sumUpLaborBySalesOffice ?? new Map<number, LaborCalculationResult>();
		const existingCollection = this.miscMetaData.travelExpensesCollection ?? [];
		const existingByOffice = new Map<number, TravelExpenses>();

		for (const expense of existingCollection) {
			existingByOffice.set(expense.salesOffice, expense);
		}

		const officeIds = new Set<number>();
		projectOfficeHours.forEach((_value, officeId) => officeIds.add(officeId));
		existingCollection.forEach(expense => officeIds.add(expense.salesOffice));

		const selectedTravelSalesOffice = this.miscMetaData.travelExpenses?.salesOffice;
		if (selectedTravelSalesOffice !== null && selectedTravelSalesOffice !== undefined) {
			officeIds.add(selectedTravelSalesOffice);
		}

		const rebuiltCollection = new TravelExpensesCollection();
		officeIds.forEach((officeId) => {
			if (!this.shouldIncludeSalesOfficeInTravelExpensesCollection(officeId)) {
				return;
			}
			const officeHours = projectOfficeHours.get(officeId) ?? this.createEmptyLaborResult();
			const existingOfficeExpense = existingByOffice.get(officeId);
			rebuiltCollection.push(this.buildTravelExpenseForOffice(officeId, officeHours, existingOfficeExpense, travelRates));
		});

		this.miscMetaData.travelExpensesCollection = rebuiltCollection;

		const updatedByOffice = new Map<number, TravelExpenses>();
		for (const expense of rebuiltCollection) {
			updatedByOffice.set(expense.salesOffice, expense);
		}

		const preferredOfficeId = selectedTravelSalesOffice ?? this.estimateData?.salesOfficeId;
		if (preferredOfficeId !== null && preferredOfficeId !== undefined && updatedByOffice.has(preferredOfficeId)) {
			this.miscMetaData.travelExpenses = updatedByOffice.get(preferredOfficeId);
		} else if (rebuiltCollection.length > CONSTANTS.ZERO) {
			this.miscMetaData.travelExpenses = rebuiltCollection[CONSTANTS.ZERO];
		} else {
			const emptyTravelExpense = new TravelExpenses();
			emptyTravelExpense.salesOffice = preferredOfficeId ?? CONSTANTS.ZERO;
			emptyTravelExpense.travelRates = travelRates;
			this.miscMetaData.travelExpenses = emptyTravelExpense;
		}
	}

	private shouldIncludeSalesOfficeInTravelExpensesCollection(salesOfficeId: number): boolean {
		const isZineSalesOffice = salesOfficeId === this.estimateMetaData?.estimateSettings?.defaultZineOffice;
		const isZTCSalesOffice = salesOfficeId === this.estimateMetaData?.estimateSettings?.defaultEngOffice;
		const isSalesOfficeExcludedFromTravel = this.isSalesOfficeExcludedFromTravel(salesOfficeId);
		const ignoreNegativeOfficeIds = salesOfficeId < 0;

		return !isZineSalesOffice && !isZTCSalesOffice && !isSalesOfficeExcludedFromTravel && !ignoreNegativeOfficeIds;
	}

	private isSalesOfficeExcludedFromTravel(_salesOfficeId: number): boolean {
		const generalProjectConfigurations: Record<string, string> = this.commonMetaDataService.getGeneralConfigurations();

		if (!generalProjectConfigurations) {
			return false;
		}

		const excludedSalesOffices = generalProjectConfigurations[CONSTANTS.SALES_OFFICES_EXCLUDED_FROM_TRAVEL];

		return excludedSalesOffices ? excludedSalesOffices.split(',').map(Number).includes(_salesOfficeId) : false;
	}

	private buildTravelExpenseForOffice(
		salesOfficeId: number,
		officeHours: LaborCalculationResult,
		existingOfficeExpense: TravelExpenses | undefined,
		travelRates: TravelRates
	): TravelExpenses {
		const officeExpense = existingOfficeExpense ? { ...existingOfficeExpense } : new TravelExpenses();
		officeExpense.salesOffice = salesOfficeId;
		officeExpense.travelRates = travelRates;

		officeExpense.pmExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.pmExpenses, officeHours.pmHours, travelRates);
		officeExpense.engExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.engExpenses, officeHours.engineeringHours, travelRates);
		officeExpense.specExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.specExpenses, officeHours.specialistHours, travelRates);
		officeExpense.mechExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.mechExpenses, officeHours.mechanicalHours, travelRates);
		officeExpense.elecExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.elecExpenses, officeHours.electricalHours, travelRates);
		officeExpense.nengExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.nengExpenses, officeHours.networkEngineeringHours, travelRates);
		officeExpense.qengExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.qengExpenses, officeHours.qualificationEngineeringHours, travelRates);
		officeExpense.cengExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.cengExpenses, officeHours.cyberSecEngineeringHours, travelRates);
		officeExpense.eengExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.eengExpenses, officeHours.energyEngineeringHours, travelRates);
		officeExpense.lengExpenses = this.updateIndividualTravelExpenseFromHours(existingOfficeExpense?.lengExpenses, officeHours.lEngineeringHours, travelRates);

		officeExpense.totalTravelCost = roundHalfEven9(
			officeExpense.pmExpenses.totalIndividualTravelCost +
			officeExpense.engExpenses.totalIndividualTravelCost +
			officeExpense.specExpenses.totalIndividualTravelCost +
			officeExpense.mechExpenses.totalIndividualTravelCost +
			officeExpense.elecExpenses.totalIndividualTravelCost +
			officeExpense.nengExpenses.totalIndividualTravelCost +
			officeExpense.qengExpenses.totalIndividualTravelCost +
			officeExpense.cengExpenses.totalIndividualTravelCost +
			officeExpense.eengExpenses.totalIndividualTravelCost +
			officeExpense.lengExpenses.totalIndividualTravelCost
		);

		return officeExpense;
	}

	private updateIndividualTravelExpenseFromHours(
		existingExpense: IndividualTravelExpenses | undefined,
		estimatedHours: number,
		travelRates: TravelRates
	): IndividualTravelExpenses {
		const updatedExpense = existingExpense ? { ...existingExpense } : new IndividualTravelExpenses();
		const sanitizedHours = estimatedHours ?? CONSTANTS.ZERO;
		updatedExpense.estimatedHours = roundHalfEven9(sanitizedHours);

		if (!updatedExpense.overrideAutoCalculate) {
			const derivedDays = roundHalfEvenToPrecision(updatedExpense.estimatedHours / CONSTANTS.NUMBER_OF_WORKING_HOURS_A_DAY, CONSTANTS.ZERO);
			updatedExpense.perDiemDays = derivedDays;
			updatedExpense.milageTrips = derivedDays;
			updatedExpense.lodgingStays = derivedDays;
			updatedExpense.parkingDays = derivedDays;
			updatedExpense.tollsDays = derivedDays;
		}

		updatedExpense.totalIndividualTravelCost = this.calculateIndividualTravelCost(updatedExpense, travelRates);
		return updatedExpense;
	}

	/**
	 * Calculates the individual travel cost for a given expense category using travel rates
	 * @param travelExpenses - The individual travel expenses (pm, eng, spec, etc.)
	 * @param travelRates - The travel rates from miscMetaData
	 * @returns The calculated total individual travel cost
	 */
	private calculateIndividualTravelCost(travelExpenses: IndividualTravelExpenses, travelRates: TravelRates): number {
		const parkingCost = roundHalfEven9(travelExpenses.parkingDays * travelRates.parking);
		const lodgingCost = roundHalfEven9(travelExpenses.lodgingStays * travelRates.lodging);
		const tollsCost = roundHalfEven9(travelExpenses.tollsDays * travelRates.tolls);
		const milageCost = roundHalfEven9(travelExpenses.milageTrips * travelRates.milage * travelRates.milageRT);
		const perDiemCost = roundHalfEven9(travelExpenses.perDiemDays * travelRates.perDiem);
		const individualTotalCost = roundHalfEven9(parkingCost + lodgingCost + perDiemCost + tollsCost + milageCost);
		return individualTotalCost;
	}

	private getProductivityReserveCost(projectProductivityReserve: number): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.productivityReserveCost ?? CONSTANTS.ZERO;
		}
		if (!this.estimateMetaData.estimateSettings.useProductivityReserve) {
			return CONSTANTS.ZERO;
		}

		return projectProductivityReserve;
	}

	private getTaxCost(_item: SQWebProject): number {
		return this.estimateMetaData.estimateTab.taxCost;
	}

	private getSubsidyCost(item: SQWebProject): number {
		return this.estimateMetaData.estimateTab.subsidy;
	}

	private applySubsidy(project: SQWebProject): void {
		const subsidyValue = this.estimateMetaData?.estimateTab?.subsidy ?? CONSTANTS.ZERO;
		const subsidyMaterial = this.estimateMetaData?.estimateTab?.subsidyMaterial;
		if (subsidyValue <= CONSTANTS.ZERO) {
			return;
		}

		let materialCategory: SQWebMaterialCategory | null = null;
		switch (subsidyMaterial) {
			case SubSidyProgramMaterialType.Siemens_Material:
				materialCategory = project.siemensMaterialCategory;
				break;
			case SubSidyProgramMaterialType.Outside_Material:
				materialCategory = project.outsideMaterialCategory;
				break;
			case SubSidyProgramMaterialType.Mechanical_Material:
				materialCategory = project.mechanicalMaterialCategory;
				break;
			case SubSidyProgramMaterialType.Electrical_Material:
				materialCategory = project.electricalMaterialCategory;
				break;
		}

		if (materialCategory) {
			materialCategory.sumUpCost -= subsidyValue;
			project.totalMaterialCategory.sumUpCost =
				project.siemensMaterialCategory.sumUpCost
				+ project.outsideMaterialCategory.sumUpCost
				+ project.mechanicalMaterialCategory.sumUpCost
				+ project.electricalMaterialCategory.sumUpCost;
		}
	}

	/**
	 * Snapshots the project material costs before subsidy and before escalation.
	 */
	private capturePreSubsidyMaterialCosts(project: SQWebProject): void {
		this.preSubsidyMaterialCosts = {
			[MaterialSubCategory.Siemens]: project.siemensMaterialCategory.sumUpCost,
			[MaterialSubCategory.Outside]: project.outsideMaterialCategory.sumUpCost,
			[MaterialSubCategory.Mechanical]: project.mechanicalMaterialCategory.sumUpCost,
			[MaterialSubCategory.Electrical]: project.electricalMaterialCategory.sumUpCost
		};
	}

	private restorePreSubsidyMaterialCosts(project: SQWebProject): void {
		project.siemensMaterialCategory.sumUpCost = this.preSubsidyMaterialCosts[MaterialSubCategory.Siemens];
		project.outsideMaterialCategory.sumUpCost = this.preSubsidyMaterialCosts[MaterialSubCategory.Outside];
		project.mechanicalMaterialCategory.sumUpCost = this.preSubsidyMaterialCosts[MaterialSubCategory.Mechanical];
		project.electricalMaterialCategory.sumUpCost = this.preSubsidyMaterialCosts[MaterialSubCategory.Electrical];
		project.totalMaterialCategory.sumUpCost =
			project.siemensMaterialCategory.sumUpCost
			+ project.outsideMaterialCategory.sumUpCost
			+ project.mechanicalMaterialCategory.sumUpCost
			+ project.electricalMaterialCategory.sumUpCost;
	}

	/**
	 * Re-applies the subsidy without re-walking the hierarchy or reading the level-wise collections.
	 * A subsidy only subtracts a scalar from one project-level material category, so restoring the
	 * pre-subsidy costs and redoing subsidy -> material escalation -> fees is enough. Labor is
	 * unaffected, and shipping & warranty are charged on the actual (pre-subsidy) cost.
	 *
	 * @returns false when no full calculation has run yet and the caller must fall back to one.
	 */
	recalculateSubsidy(): boolean {
		if (!this.preSubsidyMaterialCosts) {
			return false;
		}
		this.getProjectData();
		if (!this.project || !this.feesData || this.commonDataService.isReadOnlyMode()) {
			return false;
		}

		this.restorePreSubsidyMaterialCosts(this.project);
		this.applySubsidy(this.project);
		this.applyMaterialEscalationAndTotals(this.project);
		this.calculateFees(this.project, this.lastProductivityReserve);
		return true;
	}

	/**
	 * Gets the total shipping cost at the project level.
	 * Sums the per-material-category shipping costs that were derived during setMaterialCostAtLevel.
	 * Formula per category: materialCost * (shippingRate% / 100)
	 * Same logic as sol-estimate-material.service.ts → calculateShippingCost(dollars, rate)
	 */
	private getShippingCost(item: SQWebProject): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.totalShippingCost ?? CONSTANTS.ZERO;
		}
		return (item.siemensMaterialCategory?.shippingHandlingCost ?? CONSTANTS.ZERO)
			+ (item.outsideMaterialCategory?.shippingHandlingCost ?? CONSTANTS.ZERO)
			+ (item.mechanicalMaterialCategory?.shippingHandlingCost ?? CONSTANTS.ZERO)
			+ (item.electricalMaterialCategory?.shippingHandlingCost ?? CONSTANTS.ZERO);
	}

	/**
	 * Gets the total warranty cost at the project level.
	 * Sums the per-material-category warranty costs that were derived during setMaterialCostAtLevel.
	 * Formula per category: materialCost * ((yr1 + yr2 + yr3 + yr4 + yr5) / 100)
	 * Same logic as sol-estimate-material.service.ts → calculateWarrantyTotal(dollars, yr1..yr5)
	 */
	private getWarrantyCost(item: SQWebProject): number {
		// SAP Submittal: Return pre-calculated value from estimateTab in read-only mode
		if (this.commonDataService.isReadOnlyMode()) {
			return this.estimateMetaData.estimateTab.totalWarrantyCost ?? CONSTANTS.ZERO;
		}
		return (item.siemensMaterialCategory?.warrantyCost ?? CONSTANTS.ZERO)
			+ (item.outsideMaterialCategory?.warrantyCost ?? CONSTANTS.ZERO)
			+ (item.mechanicalMaterialCategory?.warrantyCost ?? CONSTANTS.ZERO)
			+ (item.electricalMaterialCategory?.warrantyCost ?? CONSTANTS.ZERO);
	}

	/**
	 * Calculates the shipping & handling cost for a single material category.
	 * Uses commonFunctions.calculateShippingCostForCategory (single source of truth).
	 * @param materialCost - The sumUp material cost for the category
	 * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
	 * @returns The calculated shipping cost, or 0 if GM data is unavailable
	 */
	private calculateShippingCostForCategory(
		materialCost: number,
		category: MaterialSubCategory
	): number {
		const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;
		if (!gm) { return CONSTANTS.ZERO; }
		const shippingBaseCost = this.estimateCommonFunctions.getShippingWarrantyBaseCost(materialCost, category);
		return this.estimateCommonFunctions.calculateShippingCostForCategory(shippingBaseCost, gm, category);
	}

	/**
	 * Calculates the warranty cost for a single material category.
	 * Uses commonFunctions.calculateWarrantyCostForCategory (single source of truth).
	 * @param materialCost - The sumUp material cost for the category
	 * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
	 * @returns The calculated warranty cost, or 0 if GM data is unavailable
	 */
	private calculateWarrantyCostForCategory(
		materialCost: number,
		category: MaterialSubCategory
	): number {
		const gm: EstimateGrossMargin | undefined = this.commonDataService.getOtherMetaData()?.grossMargin;
		if (!gm) { return CONSTANTS.ZERO; }
		const warrantyBaseCost = this.estimateCommonFunctions.getShippingWarrantyBaseCost(materialCost, category);
		return this.estimateCommonFunctions.calculateWarrantyCostForCategory(warrantyBaseCost, gm, category);
	}

	/**
	 * Recalculates shipping & warranty costs for all material categories
	 * after escalation has updated the sumUpCost values.
	 */
	private recalculateShippingAndWarrantyAfterEscalation(project: SQWebProject): void {
		const categories: Array<{
			materialCategory: SQWebMaterialCategory;
			categoryKey: MaterialSubCategory;
		}> = [
				{ materialCategory: project.siemensMaterialCategory, categoryKey: MaterialSubCategory.Siemens },
				{ materialCategory: project.outsideMaterialCategory, categoryKey: MaterialSubCategory.Outside },
				{ materialCategory: project.mechanicalMaterialCategory, categoryKey: MaterialSubCategory.Mechanical },
				{ materialCategory: project.electricalMaterialCategory, categoryKey: MaterialSubCategory.Electrical },
			];

		for (const { materialCategory, categoryKey } of categories) {
			if (materialCategory) {
				materialCategory.shippingHandlingCost = this.calculateShippingCostForCategory(materialCategory.sumUpCost, categoryKey);
				materialCategory.warrantyCost = this.calculateWarrantyCostForCategory(materialCategory.sumUpCost, categoryKey);
			}
		}
	}

	/**
	 * Publishes the actual (pre-promotion, pre-subsidy, un-escalated) material costs to the estimate tab.
	 * These are recomputed on every run so they always reflect the currently visible hierarchy, and are
	 * consumed by `EstimateCommonFunctionsService.getShippingWarrantyBaseCost` as the shipping &
	 * warranty base. Escalation is applied there, so the values stored here must stay raw.
	 */
	private setActualMaterialCostsOnEstimateTab(sumUpCosts: MaterialCalculationResult): void {
		const estimateTab = this.estimateMetaData?.estimateTab;
		if (!estimateTab || this.commonDataService.isReadOnlyMode()) {
			return;
		}
		estimateTab.actualSiemensMaterialCost = sumUpCosts.actualSiemensMaterialCost;
		estimateTab.actualOutsideMaterialCost = sumUpCosts.actualOutsideMaterialCost;
		estimateTab.actualMechanicalMaterialCost = sumUpCosts.actualMechanicalMaterialCost;
		estimateTab.actualElectricalMaterialCost = sumUpCosts.actualElectricalMaterialCost;
	}

	/**
	 * Sets the material cost on a hierarchy item (total + per-category breakdown)
	 * @param sumUpCosts - The total material costs including all children
	 * @param individualCosts - The material costs specific to this level only
	 */
	private setMaterialCostAtLevel(projectHierarchyItem: IProjectHierarchyItem, sumUpCosts: MaterialCalculationResult, individualCosts: MaterialCalculationResult): void {
		// Set per-category material costs
		const defaultCategory = { sumUpCost: CONSTANTS.ZERO, individualCost: CONSTANTS.ZERO, name: CONSTANTS.EMPTY_STRING, tdcPercent: CONSTANTS.ZERO, shippingHandlingCost: CONSTANTS.ZERO, warrantyCost: CONSTANTS.ZERO };

		projectHierarchyItem.totalMaterialCategory = projectHierarchyItem.totalMaterialCategory ?? { ...defaultCategory };
		projectHierarchyItem.siemensMaterialCategory = projectHierarchyItem.siemensMaterialCategory ?? { ...defaultCategory };
		projectHierarchyItem.outsideMaterialCategory = projectHierarchyItem.outsideMaterialCategory ?? { ...defaultCategory };
		projectHierarchyItem.mechanicalMaterialCategory = projectHierarchyItem.mechanicalMaterialCategory ?? { ...defaultCategory };
		projectHierarchyItem.electricalMaterialCategory = projectHierarchyItem.electricalMaterialCategory ?? { ...defaultCategory };

		projectHierarchyItem.totalMaterialCategory.sumUpCost = sumUpCosts.totalMaterialCost;
		projectHierarchyItem.totalMaterialCategory.individualCost = individualCosts.totalMaterialCost;

		projectHierarchyItem.siemensMaterialCategory.sumUpCost = sumUpCosts.siemensMaterialCost;
		projectHierarchyItem.siemensMaterialCategory.individualCost = individualCosts.siemensMaterialCost;
		projectHierarchyItem.outsideMaterialCategory.sumUpCost = sumUpCosts.outsideMaterialCost;
		projectHierarchyItem.outsideMaterialCategory.individualCost = individualCosts.outsideMaterialCost;
		projectHierarchyItem.mechanicalMaterialCategory.sumUpCost = sumUpCosts.mechanicalMaterialCost;
		projectHierarchyItem.mechanicalMaterialCategory.individualCost = individualCosts.mechanicalMaterialCost;
		projectHierarchyItem.electricalMaterialCategory.sumUpCost = sumUpCosts.electricalMaterialCost;
		projectHierarchyItem.electricalMaterialCategory.individualCost = individualCosts.electricalMaterialCost;

		projectHierarchyItem.siemensMaterialCategory.shippingHandlingCost = this.calculateShippingCostForCategory(sumUpCosts.siemensMaterialCost, MaterialSubCategory.Siemens);
		projectHierarchyItem.siemensMaterialCategory.warrantyCost = this.calculateWarrantyCostForCategory(sumUpCosts.siemensMaterialCost, MaterialSubCategory.Siemens);

		projectHierarchyItem.outsideMaterialCategory.shippingHandlingCost = this.calculateShippingCostForCategory(sumUpCosts.outsideMaterialCost, MaterialSubCategory.Outside);
		projectHierarchyItem.outsideMaterialCategory.warrantyCost = this.calculateWarrantyCostForCategory(sumUpCosts.outsideMaterialCost, MaterialSubCategory.Outside);

		projectHierarchyItem.mechanicalMaterialCategory.shippingHandlingCost = this.calculateShippingCostForCategory(sumUpCosts.mechanicalMaterialCost, MaterialSubCategory.Mechanical);
		projectHierarchyItem.mechanicalMaterialCategory.warrantyCost = this.calculateWarrantyCostForCategory(sumUpCosts.mechanicalMaterialCost, MaterialSubCategory.Mechanical);

		projectHierarchyItem.electricalMaterialCategory.shippingHandlingCost = this.calculateShippingCostForCategory(sumUpCosts.electricalMaterialCost, MaterialSubCategory.Electrical);
		projectHierarchyItem.electricalMaterialCategory.warrantyCost = this.calculateWarrantyCostForCategory(sumUpCosts.electricalMaterialCost, MaterialSubCategory.Electrical);
	}

	/**
	 * Sets the Miscellaneous cost on a hierarchy item
	 * @param sumUpMiscCosts - The total miscellaneous costs including all children
	 * @param individualMiscCosts - The miscellaneous costs specific to this level only
	 */
	private setMiscellaneousCostsAtLevel(projectHierarchyItem: IProjectHierarchyItem, sumUpMiscCosts: MiscCalculationResult, individualMiscCosts: MiscCalculationResult): void {
		projectHierarchyItem.miscellaneousCategory.sumUpCost = sumUpMiscCosts.totalNonRiskCost;
		projectHierarchyItem.miscellaneousCategory.individualCost = individualMiscCosts.totalNonRiskCost;
		projectHierarchyItem.riskCategory.sumUpCost = sumUpMiscCosts.totalRiskCost;
		projectHierarchyItem.riskCategory.individualCost = individualMiscCosts.totalRiskCost;
		projectHierarchyItem.subcontractorCategory.sumUpCost = sumUpMiscCosts.totalSubcontractorCost;
		projectHierarchyItem.subcontractorCategory.individualCost = individualMiscCosts.totalSubcontractorCost;
	}

	/**
	 * Sets the labor cost on a hierarchy item
	 * @param sumUpLaborCosts - The total labor costs including all children
	 * @param individualLaborCosts - The labor costs specific to this level only
	 */
	private setLaborCostsAtLevel(projectHierarchyItem: IProjectHierarchyItem, sumUpLaborCosts: LaborCalculationResult, individualLaborCosts: LaborCalculationResult): void {
		projectHierarchyItem.laborTotalCategory.sumUpCost = sumUpLaborCosts.totalLaborCost;
		projectHierarchyItem.laborTotalCategory.individualCost = individualLaborCosts.totalLaborCost;

		projectHierarchyItem.projectManagerCategory.sumUpCost = sumUpLaborCosts.pmLaborCost;
		projectHierarchyItem.projectManagerCategory.individualCost = individualLaborCosts.pmLaborCost;
		projectHierarchyItem.projectManagerCategory.hours = sumUpLaborCosts.pmHours;

		projectHierarchyItem.programmingCategory.sumUpCost = sumUpLaborCosts.programmingLaborCost;
		projectHierarchyItem.programmingCategory.individualCost = individualLaborCosts.programmingLaborCost;
		projectHierarchyItem.programmingCategory.hours = sumUpLaborCosts.programmingHours;

		projectHierarchyItem.graphicsCategory.sumUpCost = sumUpLaborCosts.graphicsLaborCost;
		projectHierarchyItem.graphicsCategory.individualCost = individualLaborCosts.graphicsLaborCost;
		projectHierarchyItem.graphicsCategory.hours = sumUpLaborCosts.graphicsHours;

		projectHierarchyItem.ddsCategory.sumUpCost = sumUpLaborCosts.digitalDeploymentLaborCost;
		projectHierarchyItem.ddsCategory.individualCost = individualLaborCosts.digitalDeploymentLaborCost;
		projectHierarchyItem.ddsCategory.hours = sumUpLaborCosts.digitalDeploymentHours;

		projectHierarchyItem.engineerCategory.sumUpCost = sumUpLaborCosts.engineeringLaborCost;
		projectHierarchyItem.engineerCategory.individualCost = individualLaborCosts.engineeringLaborCost;
		projectHierarchyItem.engineerCategory.hours = sumUpLaborCosts.engineeringHours;

		projectHierarchyItem.specialistCategory.sumUpCost = sumUpLaborCosts.specialistLaborCost;
		projectHierarchyItem.specialistCategory.individualCost = individualLaborCosts.specialistLaborCost;
		projectHierarchyItem.specialistCategory.hours = sumUpLaborCosts.specialistHours;

		projectHierarchyItem.mechanicalCategory.sumUpCost = sumUpLaborCosts.mechanicalLaborCost;
		projectHierarchyItem.mechanicalCategory.individualCost = individualLaborCosts.mechanicalLaborCost;
		projectHierarchyItem.mechanicalCategory.hours = sumUpLaborCosts.mechanicalHours;

		projectHierarchyItem.electricalCategory.sumUpCost = sumUpLaborCosts.electricalLaborCost;
		projectHierarchyItem.electricalCategory.individualCost = individualLaborCosts.electricalLaborCost;
		projectHierarchyItem.electricalCategory.hours = sumUpLaborCosts.electricalHours;

		projectHierarchyItem.networkEngineerCategory.sumUpCost = sumUpLaborCosts.networkEngineeringLaborCost;
		projectHierarchyItem.networkEngineerCategory.individualCost = individualLaborCosts.networkEngineeringLaborCost;
		projectHierarchyItem.networkEngineerCategory.hours = sumUpLaborCosts.networkEngineeringHours;

		projectHierarchyItem.qualificationEngineerCategory.sumUpCost = sumUpLaborCosts.qualificationEngineeringLaborCost;
		projectHierarchyItem.qualificationEngineerCategory.individualCost = individualLaborCosts.qualificationEngineeringLaborCost;
		projectHierarchyItem.qualificationEngineerCategory.hours = sumUpLaborCosts.qualificationEngineeringHours;

		projectHierarchyItem.cyberSecEngineerCategory.sumUpCost = sumUpLaborCosts.cyberSecEngineeringLaborCost;
		projectHierarchyItem.cyberSecEngineerCategory.individualCost = individualLaborCosts.cyberSecEngineeringLaborCost;
		projectHierarchyItem.cyberSecEngineerCategory.hours = sumUpLaborCosts.cyberSecEngineeringHours;

		projectHierarchyItem.energyEngineerCategory.sumUpCost = sumUpLaborCosts.energyEngineeringLaborCost;
		projectHierarchyItem.energyEngineerCategory.individualCost = individualLaborCosts.energyEngineeringLaborCost;
		projectHierarchyItem.energyEngineerCategory.hours = sumUpLaborCosts.energyEngineeringHours;

		projectHierarchyItem.lengEngineerCategory.sumUpCost = sumUpLaborCosts.lEngineeringLaborCost;
		projectHierarchyItem.lengEngineerCategory.individualCost = individualLaborCosts.lEngineeringLaborCost;
		projectHierarchyItem.lengEngineerCategory.hours = sumUpLaborCosts.lEngineeringHours;
	}

	// ── Escalation Logic ─────────────────────────────────────────────

	/**
	 * Applies escalation to material and labor costs at the project level.
	 * For each category, if escalationEnabled is true, the calculated base cost
	 * is fed into the escalation formula and the resulting escalated cost replaces
	 * the category's sumUpCost. The escalation data is also updated with the
	 * current base cost and computed escalated cost.
	 */
	private applyEscalation(project: SQWebProject): void {
		const escalationRates = this.estimateMetaData?.escalationRates;
		if (!escalationRates) {
			return;
		}

		this.applyMaterialEscalationAndTotals(project);
		this.applyLaborEscalation(project, escalationRates);

		// Recalculate total labor cost as sum of (potentially escalated) labor categories
		project.laborTotalCategory.sumUpCost =
			project.projectManagerCategory.sumUpCost +
			project.engineerCategory.sumUpCost +
			project.graphicsCategory.sumUpCost +
			project.programmingCategory.sumUpCost +
			project.specialistCategory.sumUpCost +
			project.ddsCategory.sumUpCost +
			project.mechanicalCategory.sumUpCost +
			project.electricalCategory.sumUpCost +
			project.networkEngineerCategory.sumUpCost +
			project.qualificationEngineerCategory.sumUpCost +
			project.cyberSecEngineerCategory.sumUpCost +
			project.energyEngineerCategory.sumUpCost +
			project.lengEngineerCategory.sumUpCost;
	}

	/**
	 * Escalates each material category and refreshes the derived material totals.
	 * Expects the subsidy to have already been applied - escalation is calculated on the
	 * subsidised cost, while shipping & warranty stay on the actual (pre-subsidy) cost.
	 */
	private applyMaterialEscalationAndTotals(project: SQWebProject): void {
		const escalationRates = this.estimateMetaData?.escalationRates;
		if (!escalationRates) {
			return;
		}

		this.applyMaterialEscalation(project, escalationRates);

		// Recalculate shipping & warranty costs using the (potentially escalated) material costs
		this.recalculateShippingAndWarrantyAfterEscalation(project);

		// Recalculate total material cost as sum of (potentially escalated) categories
		project.totalMaterialCategory.sumUpCost =
			project.siemensMaterialCategory.sumUpCost +
			project.outsideMaterialCategory.sumUpCost +
			project.mechanicalMaterialCategory.sumUpCost +
			project.electricalMaterialCategory.sumUpCost;
	}

	/**
	 * Applies escalation to each material category at project level.
	 * Updates the escalation data's baseCost/actualBaseCost with the calculated cost,
	 * computes the escalated cost, and replaces the category's sumUpCost.
	 */
	private applyMaterialEscalation(project: SQWebProject, escalationRates: Escalations): void {
		this.applyEscalationToCategory(
			project.siemensMaterialCategory,
			escalationRates.siemensMaterialEscalation
		);
		this.applyEscalationToCategory(
			project.outsideMaterialCategory,
			escalationRates.outsideMaterialEscalation
		);
		this.applyEscalationToCategory(
			project.mechanicalMaterialCategory,
			escalationRates.mechanicalMaterialEscalation
		);
		this.applyEscalationToCategory(
			project.electricalMaterialCategory,
			escalationRates.electricalMaterialEscalation
		);
	}

	/**
	 * Applies escalation to each labor category at project level.
	 * Updates the escalation data's baseCost/actualBaseCost with the calculated cost,
	 * computes the escalated cost, and replaces the category's sumUpCost.
	 */
	private applyLaborEscalation(project: SQWebProject, escalationRates: Escalations): void {
		this.applyEscalationToCategory(project.projectManagerCategory, escalationRates.pmLaborEscalation);
		this.applyEscalationToCategory(project.engineerCategory, escalationRates.engLaborEscalation);
		this.applyEscalationToCategory(project.graphicsCategory, escalationRates.grapLaborEscalation);
		this.applyEscalationToCategory(project.programmingCategory, escalationRates.progLaborEscalation);
		this.applyEscalationToCategory(project.specialistCategory, escalationRates.specLaborEscalation);
		this.applyEscalationToCategory(project.ddsCategory, escalationRates.ddepLaborEscalation);
		this.applyEscalationToCategory(project.mechanicalCategory, escalationRates.mechLaborEscalation);
		this.applyEscalationToCategory(project.electricalCategory, escalationRates.elecLaborEscalation);
		this.applyEscalationToCategory(project.networkEngineerCategory, escalationRates.nengLaborEscalation);
		this.applyEscalationToCategory(project.qualificationEngineerCategory, escalationRates.qengLaborEscalation);
		this.applyEscalationToCategory(project.cyberSecEngineerCategory, escalationRates.cengLaborEscalation);
		this.applyEscalationToCategory(project.energyEngineerCategory, escalationRates.eengLaborEscalation);
		this.applyEscalationToCategory(project.lengEngineerCategory, escalationRates.lengLaborEscalation);
	}

	/**
	 * Applies escalation to a single category.
	 * If escalation is enabled for this category:
	 *   1. Sets the base cost on the escalation data to the current calculated cost
	 *   2. Recalculates the escalated cost using the year percentages and rates
	 *   3. Replaces the category's sumUpCost with the escalated cost
	 * If escalation is disabled, the category cost remains unchanged and the
	 * escalation data is updated with zero escalated cost.
	 *
	 * @param category The project hierarchy category (material or labor)
	 * @param escalationData The escalation configuration for this category
	 */
	private applyEscalationToCategory(
		category: { sumUpCost: number },
		escalationData: EscalationDataDTO | undefined
	): void {
		if (!escalationData) {
			return;
		}

		// Always update the base cost reference with the current calculated cost
		escalationData.baseCost = category.sumUpCost;
		escalationData.actualBaseCost = category.sumUpCost;

		if (escalationData.escalationEnabled) {
			const escalatedCost = this.estimateCommonFunctions.calculateTotalEscalatedCost(escalationData);
			escalationData.escalatedCost = escalatedCost;

			// Replace the category cost with the escalated cost
			category.sumUpCost = escalatedCost;
		}
	}

	/**
	 * Type guard to check if an item has nodeType (i.e., is an IAreaChild)
	 */
	private isAreaChild(item: IHierarchyItem): item is IAreaChild {
		return 'nodeType' in item;
	}

	/**
	 * Type guard to check if an IAreaChild is an SQWebArea
	 */
	private isArea(item: IAreaChild): item is SQWebArea {
		return item.nodeType === CONSTANTS.NODETYPE_AREA;
	}

	/**
	 * Type guard to check if an IAreaChild is an SQWebSystem
	 */
	private isSystem(item: IAreaChild): item is SQWebSystem {
		return item.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM;
	}

	/**
	 * Type guard to check if an IAreaChild is a material-only leaf node (Controller, Panel, DesigoOptic)
	 */
	private isMaterialNode(item: IAreaChild): item is MaterialNode {
		return this.isController(item) || this.isPanel(item) || this.isDesigoOptic(item);
	}

	/**
	 * Type guard to check if an IAreaChild is an SQWebController
	 */
	private isController(item: IAreaChild): item is SQWebController {
		return item.nodeType === CONSTANTS.NODETYPE_CONTROLLER;
	}

	/**
	 * Type guard to check if an IAreaChild is an SQWebPanel
	 */
	private isPanel(item: IAreaChild): item is SQWebPanel {
		return item.nodeType === CONSTANTS.NODETYPE_PANEL;
	}

	/**
	 * Type guard to check if an IAreaChild is an SQWebDesigoOptic
	 */
	private isDesigoOptic(item: IAreaChild): item is SQWebDesigoOptic {
		return item.nodeType === CONSTANTS.NODETYPE_DESIGOOPTIC;
	}


	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Resets all estimate-scoped cached data to initial values.
	 * Called when the user closes an estimate/project.
	 */
	resetEstimateState(): void {
		this.project = null;
		this.laborMetadata = null;
		this.estimateMetaData = null;
		this.laborLevelwiseData = null;
		this.miscLevelwiseData = null;
		this.materialLevelwiseData = null;
		this.miscMetaData = null;
		this.estimateData = null;
		this.presentLaborRates = null;
		this.laborConstants = null;
		this.newEscalationRates = null;
		this.feesData = null;
		this.preSubsidyMaterialCosts = null;
		this.lastProductivityReserve = CONSTANTS.ZERO;
		this.estimateSubContractorMap = new Map();
		this.retainedSubContractorState = new Map();
		this.laborByLevelAndSalesOffice.clear();
	}

}

